import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash, GridFour, Buildings, MapTrifold, UploadSimple, X, Copy, CaretDown } from '@phosphor-icons/react';
import api from '../../utils/api';
import { toast } from 'sonner';
import { DraggableMarker } from './DraggableMarker';
import { BarnMapEditor } from './BarnMapEditor';

export const FacilityMapUpload = ({ showId }) => {
  const [mapUrl, setMapUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [drawing, setDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);
  const [savingMarkers, setSavingMarkers] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const containerRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(() => {
    api.get(`/shows/${showId}/spot-map`).then(({ data }) => {
      if (data.facility_map_url) setMapUrl(data.facility_map_url);
      if (data.barn_markers?.length > 0) setMarkers(data.barn_markers);
    }).catch(() => {});
  }, [showId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/shows/${showId}/facility-map`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMapUrl(data.facility_map_url);
      toast.success('Facility map uploaded!');
    } catch { toast.error('Failed to upload map'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/shows/${showId}/facility-map`);
      setMapUrl(null); setMarkers([]);
      toast.success('Facility map removed');
    } catch { toast.error('Failed to remove map'); }
  };

  const [rotatingFacility, setRotatingFacility] = useState(false);
  const handleRotateFacility = async (degrees) => {
    if (rotatingFacility) return;
    setRotatingFacility(true);
    try {
      const { data } = await api.post(`/shows/${showId}/rotate-facility-map`, { degrees });
      if (data.refreshed_url) setMapUrl(data.refreshed_url);
      const fresh = await api.get(`/shows/${showId}/spot-map`);
      setMarkers(fresh.data.barn_markers || []);
      toast.success(`Rotated ${degrees}°`);
    } catch {
      toast.error('Rotation failed');
    } finally {
      setRotatingFacility(false);
    }
  };

  const getPercent = (e) => {
    if (!innerRef.current) return { x: 0, y: 0 };
    const rect = innerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100))
    };
  };

  const handleMouseDown = (e) => {
    if (drawing) {
      e.preventDefault();
      const p = getPercent(e);
      setDrawStart(p);
      setDrawCurrent(p);
    } else {
      // Pan mode
      setIsPanning(true);
      setPanStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e) => {
    if (drawing && drawStart) {
      e.preventDefault();
      setDrawCurrent(getPercent(e));
    } else if (isPanning) {
      setPan({ x: e.clientX - panStart.x, y: e.clientY - panStart.y });
    }
  };

  const handleMouseUp = () => {
    if (drawing && drawStart && drawCurrent) {
      const x = Math.min(drawStart.x, drawCurrent.x);
      const y = Math.min(drawStart.y, drawCurrent.y);
      const w = Math.abs(drawCurrent.x - drawStart.x);
      const h = Math.abs(drawCurrent.y - drawStart.y);
      if (w > 1 && h > 1) {
        setMarkers([...markers, { barn: '', type: 'stall', x: +x.toFixed(2), y: +y.toFixed(2), w: +w.toFixed(2), h: +h.toFixed(2) }]);
      }
      setDrawStart(null);
      setDrawCurrent(null);
      setDrawing(false);
    }
    setIsPanning(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    setZoom(z => Math.min(Math.max(z + (e.deltaY > 0 ? -0.15 : 0.15), 0.5), 5));
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  });

  const handleTouchStart = (e) => {
    if (!drawing || e.touches.length !== 1) return;
    e.preventDefault();
    const p = getTouchPercent(e.touches[0]);
    setDrawStart(p);
    setDrawCurrent(p);
  };

  const handleTouchMove = (e) => {
    if (!drawing || !drawStart || e.touches.length !== 1) return;
    e.preventDefault();
    setDrawCurrent(getTouchPercent(e.touches[0]));
  };

  const handleTouchEnd = () => { handleMouseUp(); };

  const getTouchPercent = (touch) => {
    if (!innerRef.current) return { x: 0, y: 0 };
    const rect = innerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((touch.clientY - rect.top) / rect.height) * 100))
    };
  };

  const zoomIn = () => setZoom(z => Math.min(z + 0.3, 5));
  const zoomOut = () => setZoom(z => Math.max(z - 0.3, 0.5));
  const resetView = () => { fitToViewport(); };

  // Compute a "fit entire image inside the viewport" zoom so producers see
  // the whole facility map on load (and after pressing the reset button)
  // instead of having a wide map clipped on the right or a tall one cut
  // off at the bottom.
  const fitToViewport = () => {
    const img = imgRef.current;
    const cont = containerRef.current;
    if (!img || !cont || !img.naturalWidth || !img.naturalHeight) return;
    const cw = cont.clientWidth;
    const ch = cont.clientHeight || 500;
    const renderedW = img.clientWidth || img.naturalWidth;
    const renderedH = img.clientHeight || img.naturalHeight;
    if (renderedW <= 0 || renderedH <= 0) return;
    const fit = Math.min(cw / renderedW, ch / renderedH, 1);
    setZoom(Math.max(0.1, fit));
    setPan({ x: 0, y: 0 });
  };

  const updateMarker = (idx, field, value) => {
    const u = [...markers];
    u[idx] = { ...u[idx], [field]: value };
    setMarkers(u);
  };

  const removeMarker = (idx) => {
    setMarkers(markers.filter((_, i) => i !== idx));
    if (selectedMarker === idx) setSelectedMarker(null);
  };

  const saveMarkers = async () => {
    // Allow empty arrays through — producers must be able to clear all regions.
    // Only filter out blank/unnamed rows the user hasn't named yet.
    const valid = markers.filter(m => m.barn.trim());
    setSavingMarkers(true);
    try {
      await api.put(`/shows/${showId}/barn-markers`, { markers: valid });
      setMarkers(valid);
      toast.success(valid.length === 0 ? 'All barn regions cleared' : 'Barn regions saved!');
    } catch { toast.error('Failed to save'); }
    finally { setSavingMarkers(false); }
  };

  const previewRect = drawStart && drawCurrent ? {
    x: Math.min(drawStart.x, drawCurrent.x),
    y: Math.min(drawStart.y, drawCurrent.y),
    w: Math.abs(drawCurrent.x - drawStart.x),
    h: Math.abs(drawCurrent.y - drawStart.y)
  } : null;

  return (
    <div className="bg-[#161616] border border-[#262626] rounded-lg p-4 lg:col-span-2" data-testid="facility-map-upload">
      <div className="flex items-center gap-2 mb-3">
        <MapTrifold size={18} className="text-[#34C759]" />
        <h4 className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit' }}>Facility Map</h4>
      </div>
      <p className="text-xs text-[#555] mb-3">Upload your grounds map, then zoom in and draw rectangles over each barn/section. Stalls will overlay on the map for riders.</p>

      {mapUrl ? (
        <div>
          {/* Zoom + rotate controls */}
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <button type="button" onClick={zoomOut} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white text-sm font-bold" data-testid="facility-zoom-out">−</button>
            <span className="text-[10px] text-[#555] w-10 text-center">{Math.round(zoom * 100)}%</span>
            <button type="button" onClick={zoomIn} className="w-7 h-7 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white text-sm font-bold" data-testid="facility-zoom-in">+</button>
            <button type="button" onClick={resetView} className="px-2 py-1 rounded bg-[#0A0A0A] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white" data-testid="facility-zoom-reset">Reset</button>
            <div className="w-px h-4 bg-[#262626] mx-1" />
            <button type="button" onClick={() => handleRotateFacility(270)} disabled={rotatingFacility}
              className="px-2 py-1 rounded bg-[#0A0A0A] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
              data-testid="facility-rotate-ccw" title="Rotate counter-clockwise 90°">↺ 90°</button>
            <button type="button" onClick={() => handleRotateFacility(90)} disabled={rotatingFacility}
              className="px-2 py-1 rounded bg-[#0A0A0A] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
              data-testid="facility-rotate-cw" title="Rotate clockwise 90°">↻ 90°</button>
            <button type="button" onClick={() => handleRotateFacility(180)} disabled={rotatingFacility}
              className="px-2 py-1 rounded bg-[#0A0A0A] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
              data-testid="facility-rotate-180" title="Flip 180°">180°</button>
            <span className="text-[10px] text-[#555] ml-2">{drawing ? 'Draw mode: click + drag on map' : 'Scroll to zoom, drag to pan'}</span>
          </div>
          <div
            ref={containerRef}
            className={`relative w-full select-none overflow-hidden rounded-lg border bg-[#0A0A0A] ${drawing ? 'border-[#FF9500] cursor-crosshair' : 'border-[#262626] cursor-grab'}`}
            style={{ maxHeight: '500px', touchAction: 'none' }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            data-testid="facility-map-container"
          >
            <div
              ref={innerRef}
              style={{
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transformOrigin: '0 0',
                transition: isPanning || drawStart ? 'none' : 'transform 0.15s ease-out',
                position: 'relative',
                display: 'inline-block',
                lineHeight: 0,
              }}
            >
            <img
              ref={imgRef}
              src={`${process.env.REACT_APP_BACKEND_URL}${mapUrl}`}
              alt="Facility Map"
              className={drawing ? 'rounded-lg' : 'rounded-lg'}
              style={drawing ? { pointerEvents: 'none', display: 'block', border: 0, padding: 0, margin: 0 } : { display: 'block', border: 0, padding: 0, margin: 0 }}
              data-testid="facility-map-preview"
              draggable={false}
              onLoad={fitToViewport}
            />
            {markers.map((m, i) => (
              <DraggableMarker
                key={i}
                marker={m}
                containerRef={innerRef}
                disabled={drawing}
                selected={selectedMarker === i}
                variant={m.type === 'rv' ? 'rv' : 'stall'}
                label={m.barn || `Region ${i + 1}`}
                onClick={() => setSelectedMarker(selectedMarker === i ? null : i)}
                onChange={(u) => {
                  const next = [...markers];
                  next[i] = u;
                  setMarkers(next);
                  setSelectedMarker(i);
                }}
                testId={`map-region-${i}`}
              />
            ))}
            {previewRect && (
              <div
                className="absolute border-2 border-dashed border-[#FF9500] bg-[#FF9500]/10 rounded-sm pointer-events-none"
                style={{ left: `${previewRect.x}%`, top: `${previewRect.y}%`, width: `${previewRect.w}%`, height: `${previewRect.h}%` }}
              />
            )}
            {drawing && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#FF9500] text-black text-xs font-bold px-3 py-1 rounded-full pointer-events-none z-10">
                Click and drag to draw a region
              </div>
            )}
            </div>
          </div>

          {markers.length > 0 && (
            <div className="mt-3 space-y-1.5" data-testid="marker-list">
              <p className="text-[10px] text-[#555] uppercase tracking-wider">Regions ({markers.length})</p>
              {markers.map((m, i) => (
                <div key={i} className={`flex items-center gap-2 p-1.5 rounded ${selectedMarker === i ? 'bg-[#0096FF]/10 border border-[#0096FF]/30' : ''}`} data-testid={`marker-row-${i}`}>
                  <div className={`w-3 h-3 rounded-sm flex-shrink-0 ${m.type === 'rv' ? 'bg-[#AF52DE]' : 'bg-[#FF9500]'}`}></div>
                  <input
                    value={m.barn}
                    onChange={(e) => updateMarker(i, 'barn', e.target.value)}
                    placeholder="Section name"
                    className="flex-1 px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#0096FF]"
                    data-testid={`marker-name-${i}`}
                  />
                  <select value={m.type} onChange={(e) => updateMarker(i, 'type', e.target.value)} className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs" data-testid={`marker-type-${i}`}>
                    <option value="stall">Stall</option>
                    <option value="rv">RV</option>
                  </select>
                  <button type="button" onClick={() => removeMarker(i)} className="p-0.5 text-[#FF3B30]/60 hover:text-[#FF3B30]"><X size={12} /></button>
                </div>
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-2 mt-3">
            <button type="button" onClick={() => { setDrawing(!drawing); setSelectedMarker(null); }}
              className={`flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold transition-colors ${drawing ? 'bg-[#FF9500] text-black' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:text-white hover:border-[#FF9500]/50'}`}
              data-testid="draw-region-btn">
              <MapTrifold size={12} /> {drawing ? 'Drawing...' : 'Draw Region'}
            </button>
            {markers.length > 0 && (
              <button type="button" onClick={saveMarkers} disabled={savingMarkers}
                className="flex items-center gap-1 px-3 py-1.5 bg-[#0096FF] rounded text-xs text-white font-semibold hover:bg-[#0096FF]/80 transition-colors disabled:opacity-50"
                data-testid="save-markers-btn">
                {savingMarkers ? 'Saving...' : 'Save Regions'}
              </button>
            )}
            <label className="flex items-center gap-1 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-xs text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/50 transition-colors cursor-pointer">
              <UploadSimple size={12} /> Replace Map
              <input type="file" accept="image/*,application/pdf,.pdf" onChange={handleUpload} className="hidden" />
            </label>
            <button type="button" onClick={handleDelete} className="flex items-center gap-1 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-xs text-[#FF3B30] hover:border-[#FF3B30]/50 transition-colors" data-testid="facility-map-delete">
              <X size={12} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-[#262626] rounded-lg cursor-pointer hover:border-[#0096FF]/50 transition-colors" data-testid="facility-map-dropzone">
          <UploadSimple size={28} className="text-[#555] mb-2" />
          <span className="text-xs text-[#9E9E9E]">{uploading ? 'Uploading...' : 'Click to upload facility map'}</span>
          <span className="text-[10px] text-[#555] mt-1">JPG, PNG, or WebP</span>
          <input type="file" accept="image/*,application/pdf,.pdf" onChange={handleUpload} className="hidden" disabled={uploading} />
        </label>
      )}
    </div>
  );
};

export const ChartBuilder = ({ showId, type, label }) => {
  const [barns, setBarns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detailMapUrl, setDetailMapUrl] = useState(null);
  const [uploadingDetail, setUploadingDetail] = useState(false);
  const [detailMarkers, setDetailMarkers] = useState([]);
  const [selectedDetailMarker, setSelectedDetailMarker] = useState(null);
  const [detailDrawing, setDetailDrawing] = useState(false);
  const [detailDrawStart, setDetailDrawStart] = useState(null);
  const [detailDrawCurrent, setDetailDrawCurrent] = useState(null);
  const [savingDetailMarkers, setSavingDetailMarkers] = useState(false);
  const [detailZoom, setDetailZoom] = useState(1);
  const [detailPan, setDetailPan] = useState({ x: 0, y: 0 });
  const [detailPanning, setDetailPanning] = useState(false);
  const [detailPanStart, setDetailPanStart] = useState({ x: 0, y: 0 });
  const detailContainerRef = useRef(null);
  const detailInnerRef = useRef(null);
  const [detecting, setDetecting] = useState(false);
  const [detectedSections, setDetectedSections] = useState(null);
  const [detectingCells, setDetectingCells] = useState(false);
  const [detectedCells, setDetectedCells] = useState(null);
  const [cellStartNumber, setCellStartNumber] = useState(1);
  const [detectingNumbers, setDetectingNumbers] = useState(false);
  // "Draw Grid" mode — producer drags a rectangle, then we ask for rows/cols
  const [gridMode, setGridMode] = useState(false);
  const [pendingGridRect, setPendingGridRect] = useState(null);
  const [gridConfig, setGridConfig] = useState({ barn: '', rows: 4, cols: 8, start: 1 });
  // Producer-toggleable row-editor visibility per barn. Keyed by barn index.
  // Defaults to collapsed when a barn has >20 rows so the page stays scannable.
  const [expandedBarnRows, setExpandedBarnRows] = useState({});
  const toggleBarnRowsExpanded = (bi) => setExpandedBarnRows(s => ({ ...s, [bi]: !s[bi] }));

  useEffect(() => { loadChart(); }, [showId]);

  const loadChart = async () => {
    try {
      const { data } = await api.get(`/shows/${showId}/spot-map`);
      const map = type === 'stall' ? data.stall_map : data.rv_map;
      if (map && map.length > 0) {
        setBarns(map.map(b => ({
          barn: b.barn || 'Main',
          detail_map_url: b.detail_map_url || null,
          detail_markers: b.detail_markers || [],
          rows: (b.rows || []).map(r => ({ label: r.label, spots: r.spots.length, start_number: r.start_number || 1, custom_labels: r.custom_labels || {} }))
        })));
      }
      const dmUrl = type === 'stall' ? data.stall_detail_map_url : data.rv_detail_map_url;
      if (dmUrl) setDetailMapUrl(dmUrl);
      const dm = type === 'stall' ? data.stall_detail_markers : data.rv_detail_markers;
      if (dm?.length > 0) setDetailMarkers(dm);
    } catch { /* No chart yet */ }
    finally { setLoading(false); }
  };

  const handleDetailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingDetail(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/shows/${showId}/detail-map/${type}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDetailMapUrl(data[`${type}_detail_map_url`]);
      toast.success(`${label} detail map uploaded!`);
    } catch { toast.error('Failed to upload detail map'); }
    finally { setUploadingDetail(false); e.target.value = ''; }
  };

  const handleDetailDelete = async () => {
    try {
      await api.delete(`/shows/${showId}/detail-map/${type}`);
      setDetailMapUrl(null);
      setDetailMarkers([]);
      toast.success(`${label} detail map removed`);
    } catch { toast.error('Failed to remove detail map'); }
  };

  // Detail map drawing with zoom/pan
  const getDetailPercent = (e) => {
    if (!detailInnerRef.current) return { x: 0, y: 0 };
    const rect = detailInnerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100))
    };
  };

  const getTouchDetailPercent = (touch) => {
    if (!detailInnerRef.current) return { x: 0, y: 0 };
    const rect = detailInnerRef.current.getBoundingClientRect();
    return {
      x: Math.max(0, Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100)),
      y: Math.max(0, Math.min(100, ((touch.clientY - rect.top) / rect.height) * 100))
    };
  };

  const onDetailMouseDown = (e) => {
    if (detailDrawing || gridMode) {
      e.preventDefault();
      const p = getDetailPercent(e);
      setDetailDrawStart(p); setDetailDrawCurrent(p);
    } else {
      setDetailPanning(true);
      setDetailPanStart({ x: e.clientX - detailPan.x, y: e.clientY - detailPan.y });
    }
  };
  const onDetailMouseMove = (e) => {
    if ((detailDrawing || gridMode) && detailDrawStart) { e.preventDefault(); setDetailDrawCurrent(getDetailPercent(e)); }
    else if (detailPanning) { setDetailPan({ x: e.clientX - detailPanStart.x, y: e.clientY - detailPanStart.y }); }
  };
  const onDetailMouseUp = () => {
    if ((detailDrawing || gridMode) && detailDrawStart && detailDrawCurrent) {
      const x = Math.min(detailDrawStart.x, detailDrawCurrent.x);
      const y = Math.min(detailDrawStart.y, detailDrawCurrent.y);
      const w = Math.abs(detailDrawCurrent.x - detailDrawStart.x);
      const h = Math.abs(detailDrawCurrent.y - detailDrawStart.y);
      if (w > 1 && h > 1) {
        if (gridMode) {
          // Open the grid configuration modal with the drawn rectangle
          const highest = detailMarkers.reduce((m, mk) => {
            const parsed = parseInt((mk.barn || '').toString().split('-').pop(), 10);
            return Number.isFinite(parsed) ? Math.max(m, parsed) : m;
          }, 0);
          const nextBarnLetter = String.fromCharCode(65 + Math.min(25, new Set(detailMarkers.map(m => m.barn)).size));
          setPendingGridRect({ x: +x.toFixed(2), y: +y.toFixed(2), w: +w.toFixed(2), h: +h.toFixed(2) });
          setGridConfig(g => ({
            ...g,
            barn: g.barn || `Barn ${nextBarnLetter}`,
            start: (highest || 0) + 1 || 1,
          }));
        } else {
          setDetailMarkers([...detailMarkers, { barn: '', type, x: +x.toFixed(2), y: +y.toFixed(2), w: +w.toFixed(2), h: +h.toFixed(2) }]);
        }
      }
      setDetailDrawStart(null); setDetailDrawCurrent(null); setDetailDrawing(false); setGridMode(false);
    }
    setDetailPanning(false);
  };
  const onDetailTouchStart = (e) => { if (!(detailDrawing || gridMode) || e.touches.length !== 1) return; e.preventDefault(); const p = getTouchDetailPercent(e.touches[0]); setDetailDrawStart(p); setDetailDrawCurrent(p); };
  const onDetailTouchMove = (e) => { if (!(detailDrawing || gridMode) || !detailDrawStart || e.touches.length !== 1) return; e.preventDefault(); setDetailDrawCurrent(getTouchDetailPercent(e.touches[0])); };
  const onDetailTouchEnd = () => { onDetailMouseUp(); };

  // Commit the pending grid: subdivide the drawn rectangle into rows×cols individual stall markers.
  const confirmGrid = () => {
    if (!pendingGridRect) return;
    const rows = Math.max(1, parseInt(gridConfig.rows, 10) || 1);
    const cols = Math.max(1, parseInt(gridConfig.cols, 10) || 1);
    const start = Math.max(1, parseInt(gridConfig.start, 10) || 1);
    const barnName = (gridConfig.barn || '').trim() || 'Barn';
    const cellW = pendingGridRect.w / cols;
    const cellH = pendingGridRect.h / rows;
    const gap = 0.1; // small gap between cells
    const newMarkers = [];
    let num = start;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        newMarkers.push({
          barn: barnName,
          type,
          x: +(pendingGridRect.x + c * cellW + gap / 2).toFixed(2),
          y: +(pendingGridRect.y + r * cellH + gap / 2).toFixed(2),
          w: +(cellW - gap).toFixed(2),
          h: +(cellH - gap).toFixed(2),
          stall_number: num,
        });
        num += 1;
      }
    }
    setDetailMarkers([...detailMarkers, ...newMarkers]);
    // Also add to the stall chart so the rider flow has matching stalls
    const newRows = [];
    for (let i = 0; i < rows * cols; i++) newRows.push({ label: String(start + i), spots: 1, start_number: start + i });
    setBarns(prev => [...prev, { barn: barnName, rows: newRows }]);
    setPendingGridRect(null);
    setGridConfig({ barn: '', rows: 4, cols: 8, start: start + rows * cols });
    toast.success(`Added ${rows * cols} stalls in "${barnName}"`);
  };

  const cancelGrid = () => { setPendingGridRect(null); setGridMode(false); setDetailDrawStart(null); setDetailDrawCurrent(null); };

  const onDetailWheel = (e) => { e.preventDefault(); setDetailZoom(z => Math.min(Math.max(z + (e.deltaY > 0 ? -0.15 : 0.15), 0.5), 5)); };
  useEffect(() => {
    const el = detailContainerRef.current;
    if (!el) return;
    el.addEventListener('wheel', onDetailWheel, { passive: false });
    return () => el.removeEventListener('wheel', onDetailWheel);
  });

  const detailZoomIn = () => setDetailZoom(z => Math.min(z + 0.3, 5));
  const detailZoomOut = () => setDetailZoom(z => Math.max(z - 0.3, 0.5));
  const detailResetView = () => { fitDetailToViewport(); };

  const detailImgRef = useRef(null);
  const fitDetailToViewport = () => {
    const img = detailImgRef.current;
    const cont = detailContainerRef.current;
    if (!img || !cont || !img.naturalWidth || !img.naturalHeight) return;
    const cw = cont.clientWidth;
    const ch = cont.clientHeight || 400;
    const fit = Math.min(cw / img.naturalWidth, ch / img.naturalHeight, 1);
    setDetailZoom(Math.max(0.1, fit));
    setDetailPan({ x: 0, y: 0 });
  };

  const handleDetectStalls = async () => {
    setDetecting(true);
    setDetectedSections(null);
    try {
      // GPT-4o Vision on complex multi-barn maps (10+ barns, 400+ stalls)
      // can take 90+ seconds. Without an explicit timeout axios defaults to
      // the ingress proxy's limit and surfaces a bare "AI Detect failed".
      const { data } = await api.post(
        `/shows/${showId}/detect-spots/${type}`,
        null,
        { timeout: 180000 },
      );
      setDetectedSections(data);
      toast.success(`Detected ${data.total_sections} sections with ${data.total_stalls} ${type === 'stall' ? 'stalls' : 'RV spots'}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        toast.error('AI timed out analyzing this map (too dense). Try Detect Individual Cells or Auto-Detect Numbers instead.');
      } else {
        toast.error(detail || 'AI detection failed — try Detect Individual Cells or Auto-Detect Numbers instead.');
      }
    } finally {
      setDetecting(false);
    }
  };

  const handleApplyDetected = async (layout = 'ai') => {
    if (!detectedSections) return;
    try {
      const { data } = await api.post(`/shows/${showId}/apply-detected-spots`, {
        sections: detectedSections.sections,
        map_type: type,
        map_target: detailMapUrl ? type : 'facility',
        layout
      });
      const msg = data.layout_used === 'grid'
        ? `${data.message} — arranged as grid. Drag each region to position it on the map.`
        : data.message;
      toast.success(msg);
      setDetectedSections(null);
      await loadChart();
    } catch {
      toast.error('Failed to apply detected spots');
    }
  };

  const handleDetectCells = async () => {
    setDetectingCells(true);
    setDetectedCells(null);
    setDetectedSections(null);
    try {
      const { data } = await api.post(
        `/shows/${showId}/detect-cells/${type}`,
        null,
        { timeout: 180000 },
      );
      setDetectedCells(data);
      // If backend renormalized the stored image, force-reload it so the preview
      // overlay sits on the pixels the detector actually saw.
      if (data.refreshed_map_url) {
        setDetailMapUrl(data.refreshed_map_url);
      }
      // Auto-apply: detected cells are already the actual floor-plan
      // squares (with OCR'd numbers). Skip the manual "Apply" click.
      const startNum = parseInt(cellStartNumber, 10) || 1;
      try {
        const { data: applyData } = await api.post(`/shows/${showId}/apply-cells`, {
          barns: data.barns,
          map_type: type,
          map_target: detailMapUrl ? type : 'facility',
          start_number: startNum,
        }, { timeout: 60000 });
        toast.success(
          `Detected & applied ${data.total_cells} stalls across ${data.total_barns} barn(s) · OCR ${data.ocr_hits}/${data.ocr_total}`
            + (applyData?.message ? ` — ${applyData.message}` : ''),
        );
        setDetectedCells(null);
        await loadChart();
        return;
      } catch (applyErr) {
        toast.error(
          applyErr?.response?.data?.detail
            || `Detected ${data.total_cells} cells but auto-apply failed — click Apply to retry`,
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        toast.error('Cell detection timed out on this map. Try cropping to a single barn or use Auto-Detect Numbers.');
      } else {
        toast.error(detail || 'Cell detection failed');
      }
    } finally {
      setDetectingCells(false);
    }
  };

  const handleDetectNumbers = async () => {
    setDetectingNumbers(true);
    setDetectedCells(null);
    setDetectedSections(null);
    try {
      const { data } = await api.post(
        `/shows/${showId}/detect-numbers/${type}`,
        null,
        { timeout: 240000 },
      );
      setDetectedCells(data);
      if (data.refreshed_map_url) setDetailMapUrl(data.refreshed_map_url);
      toast.success(`Found ${data.total_cells} numbered ${type === 'rv' ? 'spots' : 'stalls'} on the map`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.code === 'ECONNABORTED' || /timeout/i.test(err.message || '')) {
        toast.error('Number detection timed out — try cropping the map to a single barn first.');
      } else {
        toast.error(detail || 'Number detection failed');
      }
    } finally {
      setDetectingNumbers(false);
    }
  };

  // Per-region detection: producer draws a rectangle (`detailMarker`) around
  // each barn on the parent map, names it, then clicks "Detect" on that row.
  // Backend crops the rectangle out of the stored image, runs OpenCV +
  // GPT-4o OCR on JUST that crop, and creates a new barn from the results.
  const [detectingRegionIdx, setDetectingRegionIdx] = useState(null);
  const [regionPreview, setRegionPreview] = useState(null); // {markerIdx, barn_name, cells, ocr_hits, ocr_total, mode}
  const [applyingRegion, setApplyingRegion] = useState(false);

  const handleDetectRegion = async (markerIdx, mode) => {
    const m = detailMarkers[markerIdx];
    if (!m) return;
    setDetectingRegionIdx(`${markerIdx}:${mode}`);
    setRegionPreview(null);
    try {
      const endpoint = mode === 'cells' ? 'detect-cells-in-region' : 'detect-numbers-in-region';
      const { data } = await api.post(
        `/shows/${showId}/${endpoint}/${type}`,
        { region: { x: m.x, y: m.y, w: m.w, h: m.h }, name: m.barn || `Barn ${markerIdx + 1}` },
        { timeout: 180000 },
      );
      // Show LIVE PREVIEW with count instead of auto-applying. Producer can
      // verify the rectangle was right before committing.
      setRegionPreview({
        markerIdx,
        barn_name: m.barn || `Barn ${markerIdx + 1}`,
        cells: data.cells || [],
        ocr_hits: data.ocr_hits || 0,
        ocr_total: data.ocr_total || (data.cells || []).length,
        mode,
      });
      toast.success(`Found ${data.total_cells} cells · review preview, then click Apply`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) {
        toast.error('Region detection timed out — try a smaller region or use Detect Cells.');
      } else {
        toast.error(detail || 'Region detection failed');
      }
    } finally {
      setDetectingRegionIdx(null);
    }
  };

  const handleApplyRegionPreview = async () => {
    if (!regionPreview) return;
    setApplyingRegion(true);
    try {
      await api.post(
        `/shows/${showId}/apply-region-cells/${type}`,
        {
          barn_name: regionPreview.barn_name,
          cells: regionPreview.cells,
          start_number: 1,
        },
      );
      toast.success(`Created barn "${regionPreview.barn_name}" with ${regionPreview.cells.length} ${type}s · Refreshing…`);
      setRegionPreview(null);
      await loadChart();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply');
    } finally {
      setApplyingRegion(false);
    }
  };

  const handleCancelRegionPreview = () => setRegionPreview(null);

  // ===== GRID FILL (A + E) =====
  // Producer draws a rectangle, names it, then says "2 rows × 17 cols,
  // start #1, reverse top row" — system creates a perfect grid distributed
  // inside the rectangle. Bypasses fragile AI detection entirely. Works for
  // any barn with a predictable row×col layout.
  const [gridFillIdx, setGridFillIdx] = useState(null);
  const [gridRows, setGridRows] = useState(2);
  const [gridCols, setGridCols] = useState(14);
  const [gridStart, setGridStart] = useState(1);
  const [gridReverseTop, setGridReverseTop] = useState(false);
  const [gridReverseEvery, setGridReverseEvery] = useState(false);
  const [gridApplying, setGridApplying] = useState(false);

  const handleOpenGridFill = (markerIdx) => {
    setGridFillIdx(markerIdx);
    setGridRows(2); setGridCols(14); setGridStart(1);
    setGridReverseTop(false); setGridReverseEvery(false);
  };

  const buildGridCells = () => {
    const m = detailMarkers[gridFillIdx];
    if (!m || gridRows < 1 || gridCols < 1) return [];
    const cells = [];
    const tileW = m.w / gridCols;
    const tileH = m.h / gridRows;
    // Shrink each cell to 80% of tile size, centered. Tile-tight (100%)
    // cells look 2x oversized vs the printed stalls. Backend applies the
    // same shrink in apply-region-cells so saved markers match the preview.
    const SHRINK = 0.80;
    const cellW = tileW * SHRINK;
    const cellH = tileH * SHRINK;
    const padX = (tileW - cellW) / 2;
    const padY = (tileH - cellH) / 2;
    let num = parseInt(gridStart, 10) || 1;
    for (let r = 0; r < gridRows; r++) {
      // Serpentine numbering: every other row reversed (column order flipped)
      const reverseThisRow = gridReverseEvery
        ? (r % 2 === 0 ? gridReverseTop : !gridReverseTop)
        : (r === 0 && gridReverseTop);
      for (let c = 0; c < gridCols; c++) {
        const col = reverseThisRow ? (gridCols - 1 - c) : c;
        cells.push({
          x: +(m.x + col * tileW + padX).toFixed(3),
          y: +(m.y + r * tileH + padY).toFixed(3),
          w: +cellW.toFixed(3),
          h: +cellH.toFixed(3),
          printed_number: num,
        });
        num++;
      }
    }
    return cells;
  };

  const handleApplyGridFill = async () => {
    const m = detailMarkers[gridFillIdx];
    if (!m) return;
    const cells = buildGridCells();
    if (cells.length === 0) { toast.error('Invalid grid'); return; }
    setGridApplying(true);
    try {
      await api.post(
        `/shows/${showId}/apply-region-cells/${type}`,
        {
          barn_name: m.barn || `Barn ${gridFillIdx + 1}`,
          cells,
          start_number: parseInt(gridStart, 10) || 1,
        },
      );
      toast.success(`Created barn "${m.barn || `Barn ${gridFillIdx + 1}`}" with ${cells.length} ${type}s · Refreshing…`);
      setGridFillIdx(null);
      await loadChart();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply grid');
    } finally {
      setGridApplying(false);
    }
  };

  // Per-barn-card Grid Fill: set rows × cols directly on an existing barn
  // card (useful when producer already typed the barn name but hasn't added
  // stalls yet). Replaces the barn's rows with the new grid.
  const [cardGridBarnIdx, setCardGridBarnIdx] = useState(null);
  const [cardGridRows, setCardGridRows] = useState(2);
  const [cardGridCols, setCardGridCols] = useState(14);
  const [cardGridStart, setCardGridStart] = useState(1);
  const [cardGridReverseTop, setCardGridReverseTop] = useState(false);
  const [cardGridReverseEvery, setCardGridReverseEvery] = useState(false);

  const openCardGrid = (bi) => {
    const b = barns[bi];
    setCardGridBarnIdx(bi);
    // Seed with existing layout if the barn already has rows
    if (b && b.rows.length > 0) {
      setCardGridRows(b.rows.length);
      setCardGridCols(b.rows[0].spots || 1);
      setCardGridStart(b.rows[0].start_number || 1);
    } else {
      setCardGridRows(2); setCardGridCols(14); setCardGridStart(1);
    }
    setCardGridReverseTop(false); setCardGridReverseEvery(false);
  };

  const applyCardGrid = () => {
    if (cardGridBarnIdx === null) return;
    const rows = [];
    let num = parseInt(cardGridStart, 10) || 1;
    for (let r = 0; r < cardGridRows; r++) {
      const reverse = cardGridReverseEvery
        ? (r % 2 === 0 ? cardGridReverseTop : !cardGridReverseTop)
        : (r === 0 && cardGridReverseTop);
      // Row starts at num; if reversed, the row still holds numbers
      // num..num+cols-1 but visually we flip column order via start_number
      // semantics — simplest is: non-reverse rows use forward start, reverse
      // rows use decreasing custom labels.
      const rowLabel = String.fromCharCode(65 + r); // A, B, C...
      const spotsInRow = cardGridCols;
      const customLabels = {};
      if (reverse) {
        for (let c = 0; c < spotsInRow; c++) {
          customLabels[c] = String(num + (spotsInRow - 1 - c));
        }
      }
      rows.push({
        label: rowLabel,
        spots: spotsInRow,
        start_number: num,
        custom_labels: customLabels,
      });
      num += spotsInRow;
    }
    setBarns(prev => {
      const next = [...prev];
      if (next[cardGridBarnIdx]) {
        next[cardGridBarnIdx] = { ...next[cardGridBarnIdx], rows };
      }
      return next;
    });
    toast.success(`Built ${cardGridRows * cardGridCols}-stall grid · Click Save Chart to persist`);
    setCardGridBarnIdx(null);
  };

  // ===== BARN LABEL AUTO-DETECT (B) =====
  // GPT-4o scans the map for visible barn labels (black squares with letters
  // like A, B, C, I, J, etc.) AND each label's surrounding stall-block
  // bounding box. Each detected label becomes a named region the producer
  // can then Grid-Fill or Detect.
  const [autoLabeling, setAutoLabeling] = useState(false);
  const handleAutoDetectBarnLabels = async () => {
    setAutoLabeling(true);
    try {
      const { data } = await api.post(
        `/shows/${showId}/detect-barn-labels/${type}`,
        null,
        { timeout: 180000 },
      );
      const found = data.barns || [];
      if (found.length === 0) {
        toast.error('No barn labels detected. Draw regions manually instead.');
        return;
      }
      // Add one region marker per detected barn. Merge with existing markers
      // rather than replacing, so producers can add AI-found barns to ones
      // they've already drawn.
      const newMarkers = found.map(b => ({
        barn: b.label || 'Barn',
        type,
        x: b.bbox.x, y: b.bbox.y, w: b.bbox.w, h: b.bbox.h,
      }));
      const merged = [...detailMarkers, ...newMarkers];
      setDetailMarkers(merged);
      try {
        await api.put(`/shows/${showId}/detail-markers/${type}`, { markers: merged });
      } catch { /* markers still shown in UI; user can manually save */ }
      toast.success(`Detected ${found.length} barns: ${found.map(b => b.label).join(', ')}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.code === 'ECONNABORTED' || /timeout/i.test(e?.message || '')) {
        toast.error('Auto-label timed out. Try drawing regions manually.');
      } else {
        toast.error(detail || 'Barn label detection failed');
      }
    } finally {
      setAutoLabeling(false);
    }
  };

  const handleApplyCells = async () => {
    if (!detectedCells) return;
    try {
      const { data } = await api.post(`/shows/${showId}/apply-cells`, {
        barns: detectedCells.barns,
        map_type: type,
        map_target: detailMapUrl ? type : 'facility',
        start_number: cellStartNumber
      });
      toast.success(data.message);
      setDetectedCells(null);
      await loadChart();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply cells');
    }
  };

  const updateDetailMarker = (idx, field, value) => { const u = [...detailMarkers]; u[idx] = { ...u[idx], [field]: value }; setDetailMarkers(u); };
  const removeDetailMarker = (idx) => { setDetailMarkers(detailMarkers.filter((_, i) => i !== idx)); };

  const saveDetailMarkers = async () => {
    // Allow empty arrays through — producers must be able to clear all detail regions.
    const valid = detailMarkers.filter(m => m.barn.trim());
    setSavingDetailMarkers(true);
    try {
      await api.put(`/shows/${showId}/detail-markers/${type}`, { markers: valid });
      setDetailMarkers(valid);
      toast.success(valid.length === 0 ? `All ${label.toLowerCase()} regions cleared` : `${label} regions saved!`);
    } catch { toast.error('Failed to save'); }
    finally { setSavingDetailMarkers(false); }
  };

  const detailPreviewRect = detailDrawStart && detailDrawCurrent ? {
    x: Math.min(detailDrawStart.x, detailDrawCurrent.x), y: Math.min(detailDrawStart.y, detailDrawCurrent.y),
    w: Math.abs(detailDrawCurrent.x - detailDrawStart.x), h: Math.abs(detailDrawCurrent.y - detailDrawStart.y)
  } : null;

  const addBarn = () => {
    const name = type === 'stall'
      ? `Barn ${String.fromCharCode(65 + barns.length)}`
      : `Section ${barns.length + 1}`;
    setBarns([...barns, { barn: name, rows: [{ label: '1', spots: 10, start_number: 1 }] }]);
  };

  const addMultipleBarns = () => {
    const count = parseInt(prompt('How many barns with the same layout?', '3'));
    if (!count || count < 1 || count > 50) return;
    const templateRows = barns.length > 0
      ? barns[barns.length - 1].rows.map(r => ({ ...r }))
      : [{ label: '1', spots: 10, start_number: 1 }];
    const newBarns = [];
    for (let i = 0; i < count; i++) {
      const idx = barns.length + i;
      const name = type === 'stall'
        ? `Barn ${String.fromCharCode(65 + idx)}`
        : `Section ${idx + 1}`;
      newBarns.push({ barn: name, rows: templateRows.map(r => ({ ...r })) });
    }
    setBarns([...barns, ...newBarns]);
  };

  const duplicateBarn = (bi) => {
    const source = barns[bi];
    const idx = barns.length;
    const name = type === 'stall'
      ? `${source.barn} (Copy)`
      : `${source.barn} (Copy)`;
    const copy = { barn: name, rows: source.rows.map(r => ({ ...r })) };
    setBarns([...barns, copy]);
  };

  const removeBarn = (bi) => setBarns(barns.filter((_, i) => i !== bi));

  const updateBarnName = (bi, name) => {
    const u = [...barns];
    u[bi] = { ...u[bi], barn: name };
    setBarns(u);
  };

  const addRow = (bi) => {
    const u = [...barns];
    const rows = u[bi].rows;
    const next = String(rows.length + 1);
    // Auto-calculate start number from the last row's end
    const lastRow = rows[rows.length - 1];
    const nextStart = lastRow ? lastRow.start_number + lastRow.spots : 1;
    u[bi] = { ...u[bi], rows: [...rows, { label: next, spots: 10, start_number: nextStart }] };
    setBarns(u);
  };

  const updateRow = (bi, ri, field, value) => {
    const u = [...barns];
    const rows = [...u[bi].rows];
    if (field === 'spots' || field === 'start_number') {
      rows[ri] = { ...rows[ri], [field]: Math.max(1, parseInt(value) || 1) };
    } else {
      rows[ri] = { ...rows[ri], [field]: value };
    }
    u[bi] = { ...u[bi], rows };
    setBarns(u);
  };

  const removeRow = (bi, ri) => {
    const u = [...barns];
    u[bi] = { ...u[bi], rows: u[bi].rows.filter((_, i) => i !== ri) };
    if (u[bi].rows.length === 0) { setBarns(u.filter((_, i) => i !== bi)); }
    else { setBarns(u); }
  };

  const [rotating, setRotating] = useState(false);

  const handleRotateDetailMap = async (degrees) => {
    if (rotating) return;
    setRotating(true);
    try {
      const { data } = await api.post(`/shows/${showId}/rotate-detail-map/${type}`, { degrees });
      if (data.refreshed_url) setDetailMapUrl(data.refreshed_url);
      // Reload markers so rotated coords render correctly
      const fresh = await api.get(`/shows/${showId}/spot-map`);
      const dm = type === 'stall' ? fresh.data.stall_detail_markers : fresh.data.rv_detail_markers;
      setDetailMarkers(dm || []);
      toast.success(`Rotated ${degrees}°`);
    } catch {
      toast.error('Rotation failed');
    } finally {
      setRotating(false);
    }
  };

  // ===== PER-BARN FLOOR-PLAN UPLOAD =====
  // Each barn can have its own reference image so riders can see the actual
  // barn layout (not just the abstract grid). The image is keyed to the
  // barn's index in the chart array on the backend.
  const [uploadingBarnIdx, setUploadingBarnIdx] = useState(null);
  const [editingBarnIdx, setEditingBarnIdx] = useState(null);

  const handleBarnDetailUpload = async (bi, file) => {
    if (!file) return;
    setUploadingBarnIdx(bi);
    try {
      // Auto-save the chart first. Without this, producers who just added
      // the barn locally hit "Barn index N not found in stall chart" because
      // the upload endpoint validates barn_idx against the persisted chart.
      const endpoint = type === 'stall' ? 'stall-chart' : 'rv-chart';
      try {
        await api.put(`/shows/${showId}/${endpoint}`, { barns });
      } catch (saveErr) {
        toast.error('Could not save chart — fix the chart first, then try uploading.');
        throw saveErr;
      }
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(
        `/shows/${showId}/barns/${type}/${bi}/detail-map`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setBarns(prev => {
        const next = [...prev];
        if (next[bi]) next[bi] = { ...next[bi], detail_map_url: data.detail_map_url };
        return next;
      });
      toast.success(`Floor plan uploaded for ${barns[bi]?.barn || 'barn'}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // Don't double-toast if the chart save above already toasted.
      if (detail && !/chart/i.test(detail)) {
        toast.error(detail);
      } else if (!detail) {
        toast.error('Failed to upload floor plan');
      }
    } finally {
      setUploadingBarnIdx(null);
    }
  };

  const handleBarnDetailDelete = async (bi) => {
    try {
      await api.delete(`/shows/${showId}/barns/${type}/${bi}/detail-map`);
      setBarns(prev => {
        const next = [...prev];
        if (next[bi]) next[bi] = { ...next[bi], detail_map_url: null };
        return next;
      });
      toast.success('Floor plan removed');
    } catch {
      toast.error('Failed to remove floor plan');
    }
  };

  const saveChart = async () => {
    setSaving(true);
    try {
      const endpoint = type === 'stall' ? 'stall-chart' : 'rv-chart';
      const { data } = await api.put(`/shows/${showId}/${endpoint}`, { barns });
      // Refresh local state from the backend's authoritative response so what the
      // producer sees matches what was actually saved (guards against stale UI).
      if (Array.isArray(data?.barns)) {
        setBarns(data.barns.map(b => ({
          barn: b.barn || 'Main',
          detail_map_url: b.detail_map_url || null,
          detail_markers: b.detail_markers || [],
          rows: (b.rows || []).map(r => ({
            label: r.label,
            spots: typeof r.spots === 'number' ? r.spots : (Array.isArray(r.spots) ? r.spots.length : 1),
            start_number: r.start_number || 1,
          })),
        })));
      }
      toast.success(`${label} chart saved (${data?.total_spots ?? '?'} spots)`);
    } catch { toast.error('Failed to save chart'); }
    finally { setSaving(false); }
  };

  const totalSpots = barns.reduce((sum, b) => sum + b.rows.reduce((s, r) => s + r.spots, 0), 0);

  if (loading) return <div className="shimmer h-8 w-32 rounded"></div>;

  return (
    <div className="bg-[#161616] border border-[#262626] rounded-lg p-4" data-testid={`${type}-chart-builder`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <GridFour size={18} className="text-[#0096FF]" />
          <h4 className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit' }}>{label} Chart</h4>
        </div>
        {totalSpots > 0 && <span className="text-xs text-[#9E9E9E]">{totalSpots} total spots</span>}
      </div>

      {/* Detail map with draw regions */}
      <div className="mb-3" data-testid={`${type}-detail-map-section`}>
        {detailMapUrl ? (
          <div>
            {/* Zoom + rotate controls */}
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <button type="button" onClick={detailZoomOut} className="w-6 h-6 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white text-xs font-bold">−</button>
              <span className="text-[9px] text-[#555] w-8 text-center">{Math.round(detailZoom * 100)}%</span>
              <button type="button" onClick={detailZoomIn} className="w-6 h-6 rounded bg-[#0A0A0A] border border-[#262626] flex items-center justify-center text-[#9E9E9E] hover:text-white text-xs font-bold">+</button>
              <button type="button" onClick={detailResetView} className="px-1.5 py-0.5 rounded bg-[#0A0A0A] border border-[#262626] text-[9px] text-[#9E9E9E] hover:text-white">Reset</button>
              <div className="w-px h-4 bg-[#262626] mx-1" />
              <button type="button" onClick={() => handleRotateDetailMap(270)} disabled={rotating}
                className="px-1.5 py-0.5 rounded bg-[#0A0A0A] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                data-testid={`${type}-rotate-ccw-btn`} title="Rotate counter-clockwise 90°">↺ 90°</button>
              <button type="button" onClick={() => handleRotateDetailMap(90)} disabled={rotating}
                className="px-1.5 py-0.5 rounded bg-[#0A0A0A] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                data-testid={`${type}-rotate-cw-btn`} title="Rotate clockwise 90°">↻ 90°</button>
              <button type="button" onClick={() => handleRotateDetailMap(180)} disabled={rotating}
                className="px-1.5 py-0.5 rounded bg-[#0A0A0A] border border-[#262626] text-[10px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                data-testid={`${type}-rotate-180-btn`} title="Flip 180°">180°</button>
              <span className="text-[9px] text-[#555] ml-auto">{detailDrawing ? 'Draw mode' : 'Scroll to zoom, drag to pan'}</span>
            </div>
            <div
              ref={detailContainerRef}
              className={`relative w-full select-none overflow-hidden rounded-lg border bg-[#0A0A0A] ${detailDrawing ? 'border-[#0096FF] cursor-crosshair' : 'border-[#262626] cursor-grab'}`}
              style={{ maxHeight: '400px', touchAction: 'none' }}
              onMouseDown={onDetailMouseDown}
              onMouseMove={onDetailMouseMove}
              onMouseUp={onDetailMouseUp}
              onMouseLeave={onDetailMouseUp}
              onTouchStart={onDetailTouchStart}
              onTouchMove={onDetailTouchMove}
              onTouchEnd={onDetailTouchEnd}
              data-testid={`${type}-detail-map-container`}
            >
              <div
                ref={detailInnerRef}
                style={{
                  transform: `translate(${detailPan.x}px, ${detailPan.y}px) scale(${detailZoom})`,
                  transformOrigin: '0 0',
                  transition: detailPanning || detailDrawStart ? 'none' : 'transform 0.15s ease-out',
                  position: 'relative'
                }}
              >
              <img
                ref={detailImgRef}
                src={`${process.env.REACT_APP_BACKEND_URL}${detailMapUrl}`}
                alt={`${label} Map`}
                className={`rounded-lg border bg-[#0A0A0A] ${detailDrawing ? 'border-[#0096FF]' : 'border-[#262626]'}`}
                style={detailDrawing ? { pointerEvents: 'none', display: 'block' } : { display: 'block' }}
                data-testid={`${type}-detail-map-preview`}
                draggable={false}
                onLoad={fitDetailToViewport}
              />
              {detailMarkers.map((m, i) => (
                <DraggableMarker
                  key={i}
                  marker={m}
                  containerRef={detailInnerRef}
                  disabled={detailDrawing || detailMarkers.length > 30}
                  selected={selectedDetailMarker === i}
                  variant="detail"
                  label={detailMarkers.length > 30 ? '' : (m.barn || `Region ${i + 1}`)}
                  onClick={() => setSelectedDetailMarker(selectedDetailMarker === i ? null : i)}
                  onChange={(u) => {
                    const next = [...detailMarkers];
                    next[i] = u;
                    setDetailMarkers(next);
                    setSelectedDetailMarker(i);
                  }}
                  testId={`${type}-detail-region-${i}`}
                />
              ))}
              {detailPreviewRect && (
                <>
                  <div className={`absolute border-2 border-dashed rounded-sm pointer-events-none ${gridMode ? 'border-[#FF9500] bg-[#FF9500]/10' : 'border-[#0096FF] bg-[#0096FF]/10'}`}
                    style={{ left: `${detailPreviewRect.x}%`, top: `${detailPreviewRect.y}%`, width: `${detailPreviewRect.w}%`, height: `${detailPreviewRect.h}%` }} />
                  {gridMode && (() => {
                    // Show faint grid lines inside the preview so producer sees how the rectangle subdivides
                    const rows = Math.max(1, parseInt(gridConfig.rows, 10) || 1);
                    const cols = Math.max(1, parseInt(gridConfig.cols, 10) || 1);
                    const lines = [];
                    for (let c = 1; c < cols; c++) {
                      lines.push(<div key={`v${c}`} className="absolute bg-[#FF9500]/40 pointer-events-none" style={{
                        left: `${detailPreviewRect.x + (detailPreviewRect.w / cols) * c}%`,
                        top: `${detailPreviewRect.y}%`,
                        width: '1px',
                        height: `${detailPreviewRect.h}%`,
                      }} />);
                    }
                    for (let r = 1; r < rows; r++) {
                      lines.push(<div key={`h${r}`} className="absolute bg-[#FF9500]/40 pointer-events-none" style={{
                        left: `${detailPreviewRect.x}%`,
                        top: `${detailPreviewRect.y + (detailPreviewRect.h / rows) * r}%`,
                        width: `${detailPreviewRect.w}%`,
                        height: '1px',
                      }} />);
                    }
                    return <>{lines}</>;
                  })()}
                </>
              )}
              {/* Live preview of OpenCV-detected cells before applying */}
              {detectedCells && detectedCells.barns.map((barn, bi) =>
                barn.cells.map((cell, ci) => (
                  <div
                    key={`cell-${bi}-${ci}`}
                    className="absolute pointer-events-none border rounded-[1px]"
                    style={{
                      left: `${cell.x}%`,
                      top: `${cell.y}%`,
                      width: `${cell.w}%`,
                      height: `${cell.h}%`,
                      borderColor: '#34C759',
                      background: 'rgba(52,199,89,0.15)',
                    }}
                  />
                ))
              )}
              {/* Live preview of REGION-scoped detection (per-barn) */}
              {regionPreview && regionPreview.cells.map((cell, ci) => (
                <div
                  key={`region-cell-${ci}`}
                  className="absolute pointer-events-none border-2 rounded-[1px] flex items-center justify-center"
                  style={{
                    left: `${cell.x}%`,
                    top: `${cell.y}%`,
                    width: `${cell.w}%`,
                    height: `${cell.h}%`,
                    borderColor: '#34C759',
                    background: 'rgba(52,199,89,0.25)',
                  }}
                >
                  {cell.printed_number != null && (
                    <span className="text-[8px] font-bold text-[#34C759] bg-[#0A0A0A]/80 px-0.5 rounded">
                      {cell.printed_number}
                    </span>
                  )}
                </div>
              ))}
              {/* Grid-Fill live preview */}
              {gridFillIdx !== null && buildGridCells().map((cell, ci) => (
                <div
                  key={`grid-cell-${ci}`}
                  className="absolute pointer-events-none border-2 rounded-[1px] flex items-center justify-center"
                  style={{
                    left: `${cell.x}%`,
                    top: `${cell.y}%`,
                    width: `${cell.w}%`,
                    height: `${cell.h}%`,
                    borderColor: '#34C759',
                    background: 'rgba(52,199,89,0.2)',
                  }}
                >
                  <span className="text-[9px] font-bold text-[#34C759] bg-[#0A0A0A]/80 px-0.5 rounded">
                    {cell.printed_number}
                  </span>
                </div>
              ))}
              {/* Alignment debug markers — show when cell detection is active so
                  user can verify the percentage coordinate system is aligned to image. */}
              {detectedCells && [
                { x: 0, y: 0, label: 'TL' },
                { x: 100, y: 0, label: 'TR' },
                { x: 0, y: 100, label: 'BL' },
                { x: 100, y: 100, label: 'BR' },
                { x: 50, y: 50, label: '•' },
              ].map((m, i) => (
                <div
                  key={`anchor-${i}`}
                  className="absolute w-3 h-3 rounded-full pointer-events-none"
                  style={{
                    left: `${m.x}%`,
                    top: `${m.y}%`,
                    transform: 'translate(-50%, -50%)',
                    background: '#FF3B30',
                    border: '2px solid white',
                    boxShadow: '0 0 0 1px rgba(0,0,0,0.4)',
                    zIndex: 20,
                  }}
                  title={m.label}
                />
              ))}
              {detailDrawing && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-[#0096FF] text-white text-xs font-bold px-3 py-1 rounded-full pointer-events-none z-10">
                  Click and drag to draw a region
                </div>
              )}
              </div>
            </div>

            {/* Per-region interactive list.
                Two shapes appear in detailMarkers:
                  - "Region-per-barn" (Auto-Detect Barns or Draw Region) where
                    each marker IS one barn. Needs Grid/Detect buttons.
                  - "Per-cell" markers (from Detect Individual Cells) where a
                    single barn has 50+ tiny cells. Not actionable per-row.
                We treat each UNIQUE barn name as one actionable row; any
                barn with >5 duplicate-name markers collapses into a summary.
            */}
            {detailMarkers.length > 0 && (() => {
              // Build one row per unique barn name; collapse duplicates
              const seen = new Map(); // name → first index
              const nameCounts = {};
              detailMarkers.forEach((m, i) => {
                const name = m.barn || `Region ${i + 1}`;
                nameCounts[name] = (nameCounts[name] || 0) + 1;
                if (!seen.has(name)) seen.set(name, i);
              });
              const uniqueRegions = [...seen.entries()].map(([name, firstIdx]) => ({
                name, firstIdx, count: nameCounts[name],
              }));
              return (
                <div className="mt-2 space-y-1" data-testid={`${type}-region-list`}>
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-[#9E9E9E]">
                      <span className="font-semibold text-white">{uniqueRegions.length}</span> region{uniqueRegions.length === 1 ? '' : 's'}
                      {detailMarkers.length !== uniqueRegions.length && <span className="text-[#555]"> · {detailMarkers.length} total markers</span>}
                    </p>
                  </div>
                  {uniqueRegions.map(({ name, firstIdx, count }) => (
                    <div key={firstIdx} className="flex items-center gap-2 flex-wrap" data-testid={`${type}-detail-marker-${firstIdx}`}>
                      <div className="w-2.5 h-2.5 rounded-sm bg-[#0096FF] flex-shrink-0"></div>
                      <input value={detailMarkers[firstIdx].barn || name}
                        onChange={(e) => updateDetailMarker(firstIdx, 'barn', e.target.value)}
                        placeholder="Barn name"
                        className="flex-1 min-w-[100px] px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#0096FF]" />
                      {count > 1 && <span className="text-[9px] text-[#555]">{count} cells</span>}
                      <button type="button" onClick={() => handleDetectRegion(firstIdx, 'cells')}
                        disabled={!!detectingRegionIdx || !!regionPreview}
                        title="Detect stall cells inside this region (OpenCV + OCR)."
                        className="px-2 py-1 bg-[#FF9500]/15 border border-[#FF9500]/40 rounded text-[10px] text-[#FF9500] hover:bg-[#FF9500]/25 disabled:opacity-50 font-semibold"
                        data-testid={`${type}-region-detect-cells-${firstIdx}`}>
                        {detectingRegionIdx === `${firstIdx}:cells` ? 'Detecting…' : '⊞ Detect'}
                      </button>
                      <button type="button" onClick={() => handleDetectRegion(firstIdx, 'numbers')}
                        disabled={!!detectingRegionIdx || !!regionPreview}
                        title="Detect printed numbers inside this region (AI)."
                        className="px-2 py-1 bg-[#AF52DE]/15 border border-[#AF52DE]/40 rounded text-[10px] text-[#AF52DE] hover:bg-[#AF52DE]/25 disabled:opacity-50 font-semibold"
                        data-testid={`${type}-region-detect-numbers-${firstIdx}`}>
                        {detectingRegionIdx === `${firstIdx}:numbers` ? 'Reading…' : '🔢 Read'}
                      </button>
                      <button type="button" onClick={() => handleOpenGridFill(firstIdx)}
                        disabled={!!detectingRegionIdx || !!regionPreview}
                        title="Create a rows × cols grid inside this region (no AI needed)."
                        className="px-2 py-1 bg-[#34C759]/15 border border-[#34C759]/40 rounded text-[10px] text-[#34C759] hover:bg-[#34C759]/25 disabled:opacity-50 font-semibold"
                        data-testid={`${type}-region-grid-fill-${firstIdx}`}>
                        ▦ Grid
                      </button>
                      <button type="button" onClick={() => removeDetailMarker(firstIdx)}
                        className="p-0.5 text-[#FF3B30]/60 hover:text-[#FF3B30]">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Grid-Fill panel — no AI, producer specifies rows × cols */}
            {gridFillIdx !== null && detailMarkers[gridFillIdx] && (() => {
              const m = detailMarkers[gridFillIdx];
              const gridCells = buildGridCells();
              return (
                <div className="mt-3 p-3 bg-[#0D1A0D] border border-[#34C759]/40 rounded-lg" data-testid={`${type}-grid-fill-panel`}>
                  <p className="text-xs font-bold text-[#34C759] mb-2">Grid Fill: {m.barn || `Region ${gridFillIdx + 1}`}</p>
                  <p className="text-[11px] text-[#9E9E9E] mb-2 leading-relaxed">
                    Create an exact grid inside this rectangle. No AI detection — use this when you know the barn's layout (e.g. Barn I = 2 rows × 17 stalls).
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <label className="text-[11px] text-[#9E9E9E]">Rows
                      <input type="number" min="1" max="20" value={gridRows} onChange={(e) => setGridRows(parseInt(e.target.value, 10) || 1)}
                        className="w-14 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                        data-testid={`${type}-grid-rows`} />
                    </label>
                    <label className="text-[11px] text-[#9E9E9E]">× Cols
                      <input type="number" min="1" max="100" value={gridCols} onChange={(e) => setGridCols(parseInt(e.target.value, 10) || 1)}
                        className="w-14 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                        data-testid={`${type}-grid-cols`} />
                    </label>
                    <label className="text-[11px] text-[#9E9E9E]">Start #
                      <input type="number" min="1" value={gridStart} onChange={(e) => setGridStart(parseInt(e.target.value, 10) || 1)}
                        className="w-16 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                        data-testid={`${type}-grid-start`} />
                    </label>
                    <span className="text-[11px] text-[#34C759] font-bold">= {gridRows * gridCols} {type === 'rv' ? 'spots' : 'stalls'}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <label className="text-[11px] text-[#9E9E9E] flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={gridReverseTop} onChange={(e) => setGridReverseTop(e.target.checked)} data-testid={`${type}-grid-reverse-top`} />
                      <span>Reverse first row <span className="text-[#555]">(e.g. top row goes 17→1, then 18→34 bottom)</span></span>
                    </label>
                    <label className="text-[11px] text-[#9E9E9E] flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={gridReverseEvery} onChange={(e) => setGridReverseEvery(e.target.checked)} data-testid={`${type}-grid-reverse-every`} />
                      <span>Serpentine <span className="text-[#555]">(alternate every row)</span></span>
                    </label>
                  </div>
                  {gridCells.length > 0 && (
                    <p className="text-[10px] text-[#9E9E9E] mb-2">
                      Preview: first 5 = {gridCells.slice(0, 5).map(c => c.printed_number).join(', ')}
                      {gridCells.length > 10 && <>… last 3 = {gridCells.slice(-3).map(c => c.printed_number).join(', ')}</>}
                    </p>
                  )}
                  <div className="flex gap-2">
                    <button type="button" onClick={handleApplyGridFill} disabled={gridApplying}
                      className="px-3 py-1.5 bg-[#34C759] rounded text-[11px] text-black font-bold hover:bg-[#34C759]/80 disabled:opacity-50"
                      data-testid={`${type}-grid-fill-apply`}>
                      {gridApplying ? 'Creating…' : `✓ Create ${gridRows * gridCols}-stall grid`}
                    </button>
                    <button type="button" onClick={() => setGridFillIdx(null)} disabled={gridApplying}
                      className="px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                      data-testid={`${type}-grid-fill-cancel`}>
                      Cancel
                    </button>
                  </div>
                </div>
              );
            })()}

            {/* Region detection live preview — count + apply/cancel */}
            {regionPreview && (
              <div className="mt-3 p-3 bg-[#0D1A0D] border border-[#34C759]/40 rounded-lg" data-testid={`${type}-region-preview`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-bold text-[#34C759]">
                    Preview: {regionPreview.cells.length} {type === 'rv' ? 'spots' : 'stalls'} found in "{regionPreview.barn_name}"
                  </span>
                  <span className="text-[10px] text-[#9E9E9E]">· OCR {regionPreview.ocr_hits}/{regionPreview.ocr_total}</span>
                </div>
                <p className="text-[11px] text-[#9E9E9E] mb-2">
                  Green boxes on the map above show where stalls will land. Numbers shown are what the AI read from the image.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={handleApplyRegionPreview}
                    disabled={applyingRegion || regionPreview.cells.length === 0}
                    className="px-3 py-1.5 bg-[#34C759] rounded text-[11px] text-black font-bold hover:bg-[#34C759]/80 disabled:opacity-50"
                    data-testid={`${type}-region-preview-apply`}
                  >
                    {applyingRegion ? 'Applying…' : `✓ Apply — Create barn "${regionPreview.barn_name}"`}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelRegionPreview}
                    disabled={applyingRegion}
                    className="px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-[11px] text-[#9E9E9E] hover:text-white disabled:opacity-50"
                    data-testid={`${type}-region-preview-cancel`}
                  >
                    Cancel
                  </button>
                  <span className="text-[10px] text-[#555] self-center ml-1">
                    Wrong rectangle? Cancel, redraw, and try again.
                  </span>
                </div>
              </div>
            )}
            {/* (Old summary block removed — unified list above handles any count.) */}

            <div className="flex flex-wrap gap-2 mt-2">
              <button type="button" onClick={() => { setDetailDrawing(!detailDrawing); setGridMode(false); }}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors ${detailDrawing ? 'bg-[#0096FF] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:text-white'}`}
                data-testid={`${type}-detail-draw-btn`}>
                <MapTrifold size={10} /> {detailDrawing ? 'Drawing...' : 'Draw Region'}
              </button>
              <button type="button" onClick={() => { setGridMode(!gridMode); setDetailDrawing(false); }}
                className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold transition-colors ${gridMode ? 'bg-[#FF9500] text-white' : 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:text-white'}`}
                data-testid={`${type}-draw-grid-btn`}
                title="Draw a rectangle, then choose rows × cols to auto-create that many stalls">
                <GridFour size={10} /> {gridMode ? 'Drawing grid...' : 'Draw Grid'}
              </button>
              {detailMarkers.length > 0 && (
                <button type="button" onClick={saveDetailMarkers} disabled={savingDetailMarkers}
                  className="px-2 py-1 bg-[#0096FF] rounded text-[10px] text-white font-semibold hover:bg-[#0096FF]/80 disabled:opacity-50"
                  data-testid={`${type}-detail-save-btn`}>
                  {savingDetailMarkers ? 'Saving...' : 'Save Regions'}
                </button>
              )}
              <label className="px-2 py-1 text-[10px] text-[#9E9E9E] hover:text-white cursor-pointer bg-[#0A0A0A] border border-[#262626] rounded">
                Replace <input type="file" accept="image/*,application/pdf,.pdf" onChange={handleDetailUpload} className="hidden" />
              </label>
              <button type="button" onClick={handleDetailDelete} className="px-2 py-1 text-[10px] text-[#FF3B30] bg-[#0A0A0A] border border-[#262626] rounded">Remove</button>
              <button type="button" onClick={handleDetectStalls} disabled={detecting || detectingCells || detectingNumbers}
                  className="flex items-center gap-1 px-2 py-1 bg-[#34C759] rounded text-[10px] text-white font-semibold hover:bg-[#34C759]/80 disabled:opacity-50"
                  data-testid={`${type}-auto-detect-btn`}>
                  {detecting ? 'Analyzing...' : `Auto-Detect ${type === 'stall' ? 'Stalls' : 'RV Spots'}`}
              </button>
              <button type="button" onClick={handleDetectCells} disabled={detecting || detectingCells || detectingNumbers}
                  className="flex items-center gap-1 px-2 py-1 bg-[#0096FF] rounded text-[10px] text-white font-semibold hover:bg-[#0096FF]/80 disabled:opacity-50"
                  data-testid={`${type}-detect-cells-btn`}
                  title="Scan the map for every printed stall box and create one clickable stall per box">
                  {detectingCells ? 'Scanning...' : 'Detect Individual Cells'}
              </button>
              <button type="button" onClick={handleDetectNumbers} disabled={detecting || detectingCells || detectingNumbers}
                  className="flex items-center gap-1 px-2 py-1 bg-[#AF52DE] rounded text-[10px] text-white font-semibold hover:bg-[#AF52DE]/80 disabled:opacity-50"
                  data-testid={`${type}-detect-numbers-btn`}
                  title="Scan the map for every printed number (no rectangles needed) — best for RV maps that just list numbered spots along a lane">
                  {detectingNumbers ? 'Reading numbers...' : 'Auto-Detect Numbers'}
              </button>
              <button type="button" onClick={handleAutoDetectBarnLabels} disabled={autoLabeling || detecting || detectingCells || detectingNumbers}
                  className="flex items-center gap-1 px-2 py-1 bg-[#FF9500] rounded text-[10px] text-white font-semibold hover:bg-[#FF9500]/80 disabled:opacity-50"
                  data-testid={`${type}-auto-detect-barns-btn`}
                  title="Find every labeled barn (A, B, C, I, J...) on the map and create a named region for each. Then Grid-Fill each region.">
                  {autoLabeling ? 'Finding barns...' : '🏷 Auto-Detect Barns'}
              </button>
            </div>

            {/* Tip about per-region detection — only shown when no detection
                results are open, otherwise it's clutter. */}
            {!detectedSections && !detectedCells && !regionPreview && gridFillIdx === null && (
              <div className="mt-2 px-2.5 py-1.5 bg-[#0096FF]/5 border border-[#0096FF]/20 rounded text-[10px] text-[#9E9E9E] leading-relaxed space-y-1">
                <p><span className="text-[#FF9500] font-semibold">Fastest path for dense multi-barn maps:</span> Click <span className="text-white font-semibold">🏷 Auto-Detect Barns</span> above — it finds every labeled barn and creates a named region for each. Then click <span className="text-[#34C759] font-semibold">▦ Grid</span> on each region and enter rows × cols (e.g. 2 × 17 for Barn I). Done in ~2 min.</p>
                <p className="text-[#555]">Or: <span className="text-white">Draw Region</span> → name → <span className="text-[#FF9500]">⊞ Detect</span> (AI) / <span className="text-[#34C759]">▦ Grid</span> (manual rows × cols).</p>
              </div>
            )}

            {/* AI Detection Results */}
            {detectedSections && (
              <div className="mt-3 bg-[#0D1A0D] border border-[#34C759]/30 rounded-lg p-3" data-testid="detected-sections">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-[#34C759]">AI Detected {detectedSections.total_sections} sections ({detectedSections.total_stalls} {type === 'stall' ? 'stalls' : 'RV spots'})</p>
                  <button type="button" onClick={() => setDetectedSections(null)}
                    className="px-2 py-1 text-[10px] text-[#9E9E9E] hover:text-white">
                    Dismiss
                  </button>
                </div>

                {detectedSections.coords_unreliable && (
                  <div className="mb-2 text-[10px] text-[#FF9500] bg-[#FF9500]/10 border border-[#FF9500]/30 rounded px-2 py-1.5">
                    <strong>Heads up:</strong> AI read the stall numbers, but its position guesses look off (maps like this are tricky for vision AI). Use <strong>Arrange as Grid</strong> and then drag each region into place with the resize handles.
                  </div>
                )}

                <div className="flex gap-2 mb-2">
                  <button type="button" onClick={() => handleApplyDetected('grid')}
                    className="flex-1 px-3 py-1.5 bg-[#34C759] rounded text-[10px] text-white font-semibold hover:bg-[#34C759]/80"
                    data-testid="apply-grid-btn">
                    Arrange as Grid
                  </button>
                  <button type="button" onClick={() => handleApplyDetected('ai')}
                    className={`flex-1 px-3 py-1.5 rounded text-[10px] font-semibold ${detectedSections.coords_unreliable ? 'bg-[#0A0A0A] border border-[#262626] text-[#9E9E9E] hover:text-white' : 'bg-[#0096FF] text-white hover:bg-[#0096FF]/80'}`}
                    data-testid="apply-ai-btn">
                    Use AI Positions
                  </button>
                </div>

                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {detectedSections.sections.map((s, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px] bg-[#0A0A0A] rounded px-2 py-1">
                      <span className="text-white font-semibold">{s.name}</span>
                      <span className="text-[#9E9E9E]">
                        {s.stall_count} stalls ({s.start_number}-{s.start_number + s.stall_count - 1})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* OpenCV Cell Detection Results */}
            {detectedCells && (
              <div className="mt-3 bg-[#0A1A2A] border border-[#0096FF]/30 rounded-lg p-3" data-testid="detected-cells">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-bold text-[#0096FF]">
                    Detected {detectedCells.total_cells} individual cells across {detectedCells.total_barns} barn(s)
                  </p>
                  <button type="button" onClick={() => setDetectedCells(null)}
                    className="px-2 py-1 text-[10px] text-[#9E9E9E] hover:text-white">
                    Dismiss
                  </button>
                </div>
                <p className="text-[10px] text-[#9E9E9E] mb-2">
                  Green boxes on the map show what was detected. Each cell becomes one clickable stall on the rider's map.
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-[10px] text-[#9E9E9E]">Start numbering at:</label>
                  <input
                    type="number"
                    min="1"
                    value={cellStartNumber}
                    onChange={(e) => setCellStartNumber(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-20 px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-xs focus:outline-none focus:border-[#0096FF]"
                    data-testid="cell-start-number-input"
                  />
                  <button type="button" onClick={handleApplyCells}
                    className="ml-auto px-3 py-1.5 bg-[#0096FF] rounded text-[10px] text-white font-semibold hover:bg-[#0096FF]/80"
                    data-testid="apply-cells-btn">
                    Apply {detectedCells.total_cells} Cells
                  </button>
                </div>
                <div className="text-[10px] text-[#FF9500] bg-[#FF9500]/10 border border-[#FF9500]/30 rounded px-2 py-1.5">
                  <strong>Note:</strong> Cells are numbered in reading order (top-to-bottom, left-to-right). After applying, you can rename barns or adjust cells individually.
                </div>
              </div>
            )}
          </div>
        ) : (
          <label className="flex items-center gap-2 p-2.5 border border-dashed border-[#262626] rounded-lg cursor-pointer hover:border-[#0096FF]/40 transition-colors" data-testid={`${type}-detail-map-upload`}>
            <UploadSimple size={14} className="text-[#555]" />
            <span className="text-[10px] text-[#9E9E9E]">{uploadingDetail ? 'Uploading...' : `Upload ${label.toLowerCase()} map (draw regions to overlay stalls)`}</span>
            <input type="file" accept="image/*,application/pdf,.pdf" onChange={handleDetailUpload} className="hidden" disabled={uploadingDetail} />
          </label>
        )}
      </div>

      {barns.length === 0 ? (
        <p className="text-xs text-[#555] mb-3">No {label.toLowerCase()}s defined yet. Add a barn/section to start.</p>
      ) : (
        <div className="space-y-4 mb-3">
          {/* Bulk action: fit ALL barns' markers to their printed cells in one shot. */}
          {(() => {
            const barnsWithMarkers = barns.filter(b => (b.detail_markers || []).length > 0).length;
            if (barnsWithMarkers === 0) return null;
            return (
              <div className="flex items-center justify-between bg-[#0D1A0D] border border-[#34C759]/30 rounded-lg px-3 py-2" data-testid={`${type}-fit-all-banner`}>
                <span className="text-[11px] text-[#9E9E9E]">
                  <span className="text-[#34C759] font-semibold">Bulk fix:</span> {barnsWithMarkers} barn{barnsWithMarkers === 1 ? '' : 's'} have placed markers — run "Fit to Squares" across all of them.
                </span>
                <button
                  type="button"
                  onClick={async () => {
                    const targets = barns
                      .map((b, idx) => ({ b, idx }))
                      .filter(({ b }) => (b.detail_markers || []).length > 0);
                    if (targets.length === 0) return;
                    const ok = window.confirm(
                      `Run "Fit to Squares" on ${targets.length} barn${targets.length === 1 ? '' : 's'}? `
                        + `This rewrites each barn's marker sizes to match the printed cells on its floor plan. Existing stall numbers are preserved.`,
                    );
                    if (!ok) return;
                    let fitted = 0;
                    let failed = 0;
                    let totalMarkers = 0;
                    for (const { b, idx } of targets) {
                      try {
                        const { data } = await api.post(
                          `/shows/${showId}/barns/${type}/${idx}/fit-markers-to-printed-cells`,
                          null,
                          { timeout: 90000 },
                        );
                        fitted += data.fitted || 0;
                        totalMarkers += (data.fitted || 0) + (data.unchanged || 0);
                      } catch {
                        failed += 1;
                      }
                    }
                    if (failed === 0) {
                      toast.success(`Fit ${fitted}/${totalMarkers} markers across ${targets.length} barns`);
                    } else {
                      toast.error(`Fit ${fitted}/${totalMarkers} markers · ${failed} barn${failed === 1 ? '' : 's'} failed`);
                    }
                    await loadChart();
                  }}
                  className="px-3 py-1 bg-[#34C759]/15 border border-[#34C759]/50 rounded text-[11px] text-[#34C759] hover:bg-[#34C759]/25 font-semibold flex items-center gap-1"
                  data-testid={`${type}-fit-all-barns`}
                >
                  ⊟ Fit All Barns
                </button>
                {/* v227 — Collapse All / Expand All for row editors */}
                <button
                  type="button"
                  onClick={() => {
                    const next = {};
                    barns.forEach((_, i) => { next[i] = false; });
                    setExpandedBarnRows(next);
                  }}
                  className="px-3 py-1 bg-[#525252]/20 border border-[#525252]/50 rounded text-[11px] text-[#9E9E9E] hover:bg-[#525252]/30 font-semibold"
                  data-testid={`${type}-collapse-all-barns`}
                  title="Hide row editors for every barn"
                >
                  Collapse All
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = {};
                    barns.forEach((_, i) => { next[i] = true; });
                    setExpandedBarnRows(next);
                  }}
                  className="px-3 py-1 bg-[#0096FF]/15 border border-[#0096FF]/50 rounded text-[11px] text-[#0096FF] hover:bg-[#0096FF]/25 font-semibold"
                  data-testid={`${type}-expand-all-barns`}
                  title="Show row editors for every barn"
                >
                  Expand All
                </button>
              </div>
            );
          })()}
          {barns.map((barn, bi) => {
            // When a barn has many rows (hundreds, e.g. from Detect Individual Cells),
            // showing every row-editor is unwieldy. Collapse to a summary with first/last info.
            const manyRows = barn.rows.length > 20;
            return (
            <div key={bi} className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3" data-testid={`${type}-barn-${bi}`}>
              <div className="flex items-center gap-2 mb-2">
                <Buildings size={14} className="text-[#FF9500] flex-shrink-0" />
                <input
                  value={barn.barn}
                  onChange={(e) => updateBarnName(bi, e.target.value)}
                  className="flex-1 px-2 py-1 bg-transparent border border-[#262626] rounded text-white text-sm font-semibold focus:outline-none focus:border-[#0096FF]"
                  placeholder="Barn name"
                  data-testid={`${type}-barn-name-${bi}`}
                />
                <button
                  type="button"
                  onClick={() => openCardGrid(bi)}
                  className="px-2 py-1 bg-[#34C759]/15 border border-[#34C759]/40 rounded text-[10px] text-[#34C759] hover:bg-[#34C759]/25 font-semibold flex items-center gap-1"
                  title="Build this barn's layout from rows × cols (replaces existing rows)."
                  data-testid={`${type}-barn-grid-fill-${bi}`}
                >
                  ▦ Grid
                </button>
                <button type="button" onClick={() => duplicateBarn(bi)} className="p-1 text-[#0096FF] hover:bg-[#0096FF]/10 rounded" data-testid={`${type}-barn-duplicate-${bi}`} title="Duplicate barn">
                  <Copy size={14} />
                </button>
                <button type="button" onClick={() => removeBarn(bi)} className="p-1 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded" data-testid={`${type}-barn-remove-${bi}`}>
                  <Trash size={14} />
                </button>
              </div>

              {/* Inline Grid-Fill panel — card-level */}
              {cardGridBarnIdx === bi && (
                <div className="mb-3 p-2.5 bg-[#0D1A0D] border border-[#34C759]/40 rounded" data-testid={`${type}-card-grid-panel-${bi}`}>
                  <p className="text-[11px] text-[#9E9E9E] mb-2">
                    Build this barn's layout from rows × cols. {barn.rows.length > 0 && <span className="text-[#FF9500]">Note: this replaces the barn's existing {barn.rows.length} row(s).</span>}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <label className="text-[11px] text-[#9E9E9E]">Rows
                      <input type="number" min="1" max="20" value={cardGridRows}
                        onChange={(e) => setCardGridRows(parseInt(e.target.value, 10) || 1)}
                        className="w-14 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                        data-testid={`${type}-card-grid-rows-${bi}`} />
                    </label>
                    <label className="text-[11px] text-[#9E9E9E]">× Cols
                      <input type="number" min="1" max="100" value={cardGridCols}
                        onChange={(e) => setCardGridCols(parseInt(e.target.value, 10) || 1)}
                        className="w-14 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                        data-testid={`${type}-card-grid-cols-${bi}`} />
                    </label>
                    <label className="text-[11px] text-[#9E9E9E]">Start #
                      <input type="number" min="1" value={cardGridStart}
                        onChange={(e) => setCardGridStart(parseInt(e.target.value, 10) || 1)}
                        className="w-16 ml-1 px-1.5 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-white text-[11px] text-center"
                        data-testid={`${type}-card-grid-start-${bi}`} />
                    </label>
                    <span className="text-[11px] text-[#34C759] font-bold">= {cardGridRows * cardGridCols} stalls</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <label className="text-[10px] text-[#9E9E9E] flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cardGridReverseTop}
                        onChange={(e) => setCardGridReverseTop(e.target.checked)}
                        data-testid={`${type}-card-grid-reverse-top-${bi}`} />
                      <span>Reverse first row</span>
                    </label>
                    <label className="text-[10px] text-[#9E9E9E] flex items-center gap-1 cursor-pointer">
                      <input type="checkbox" checked={cardGridReverseEvery}
                        onChange={(e) => setCardGridReverseEvery(e.target.checked)}
                        data-testid={`${type}-card-grid-reverse-every-${bi}`} />
                      <span>Serpentine</span>
                    </label>
                  </div>
                  <div className="flex gap-2">
                    <button type="button" onClick={applyCardGrid}
                      className="px-3 py-1 bg-[#34C759] rounded text-[10px] text-black font-bold hover:bg-[#34C759]/80"
                      data-testid={`${type}-card-grid-apply-${bi}`}>
                      ✓ Build {cardGridRows * cardGridCols}-stall grid
                    </button>
                    <button type="button" onClick={() => setCardGridBarnIdx(null)}
                      className="px-3 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[10px] text-[#9E9E9E] hover:text-white"
                      data-testid={`${type}-card-grid-cancel-${bi}`}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Per-barn floor plan reference image */}
              <div className="mb-2 flex items-center gap-2 px-2 py-1.5 bg-[#0F0F0F] border border-[#1A1A1A] rounded" data-testid={`${type}-barn-floorplan-${bi}`}>
                {barn.detail_map_url ? (
                  <>
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${barn.detail_map_url}`}
                      alt={`${barn.barn} floor plan`}
                      className="w-12 h-12 object-cover rounded border border-[#262626] flex-shrink-0"
                      data-testid={`${type}-barn-floorplan-thumb-${bi}`}
                    />
                    <span className="text-[10px] flex-1">
                      <span className="text-[#34C759]">Floor plan attached</span>
                      {(barn.detail_markers || []).length === 0 && (
                        <span className="text-[#FF9500] block">⚠ No stalls placed yet — click "Place Stalls"</span>
                      )}
                      {(barn.detail_markers || []).length === 1 && (
                        <span className="text-[#9E9E9E] block">Auto-tile mode · {barn.rows.reduce((s, r) => s + r.spots, 0)} stalls inside one rectangle</span>
                      )}
                      {(barn.detail_markers || []).length > 1 && (
                        <span className="text-[#9E9E9E] block">Per-stall mode · {(barn.detail_markers || []).length} positions placed</span>
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditingBarnIdx(bi)}
                      className="px-2 py-1 bg-[#0096FF]/10 border border-[#0096FF]/40 rounded text-[10px] text-[#0096FF] hover:bg-[#0096FF]/20 flex items-center gap-1"
                      data-testid={`${type}-barn-place-stalls-${bi}`}
                    >
                      <MapTrifold size={10} /> Place Stalls
                    </button>
                    {(barn.detail_markers || []).length > 0 && (
                      <>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const { data } = await api.post(
                                `/shows/${showId}/barns/${type}/${bi}/fit-markers-to-printed-cells`,
                                null,
                                { timeout: 60000 },
                              );
                              toast.success(data.message || 'Fit markers to printed cells');
                              await loadChart();
                            } catch (err) {
                              toast.error(err?.response?.data?.detail || 'Fit failed');
                            }
                          }}
                          title="Look INSIDE each marker's bbox for the printed cell (white interior) and shrink the marker to its exact pixel size. Fixes oversized markers that overlap multiple stalls."
                          className="px-2 py-1 bg-[#34C759]/10 border border-[#34C759]/40 rounded text-[10px] text-[#34C759] hover:bg-[#34C759]/20 flex items-center gap-1"
                          data-testid={`${type}-barn-fit-cells-${bi}`}
                        >
                          ⊟ Fit to Squares
                        </button>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const { data } = await api.post(
                                `/shows/${showId}/barns/${type}/${bi}/snap-markers-to-cells`,
                                { markers: barn.detail_markers || [] },
                                { timeout: 240000 },
                              );
                              toast.success(data.message || `Snapped ${data.snapped_to_cell} markers`);
                              await loadChart();
                            } catch (err) {
                              if (err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '')) {
                                toast.error('Snap timed out (OCR can take 2-4 min on dense maps).');
                              } else {
                                toast.error(err?.response?.data?.detail || 'Snap failed');
                              }
                            }
                          }}
                          title="Detect actual stall squares + OCR each printed number, then snap markers to match (size, position, and stall number)."
                          className="px-2 py-1 bg-[#0096FF]/10 border border-[#0096FF]/40 rounded text-[10px] text-[#0096FF] hover:bg-[#0096FF]/20 flex items-center gap-1"
                          data-testid={`${type}-barn-snap-cells-${bi}`}
                        >
                          ⊡ Snap to Squares
                        </button>
                      </>
                    )}
                    <label className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[10px] text-[#9E9E9E] hover:text-white hover:border-[#0096FF]/50 cursor-pointer">
                      Replace
                      <input
                        type="file"
                        accept="image/*,application/pdf,.pdf"
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleBarnDetailUpload(bi, f); }}
                        className="hidden"
                        data-testid={`${type}-barn-floorplan-replace-${bi}`}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => handleBarnDetailDelete(bi)}
                      className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[10px] text-[#FF3B30] hover:border-[#FF3B30]/50"
                      data-testid={`${type}-barn-floorplan-remove-${bi}`}
                    >
                      Remove
                    </button>
                  </>
                ) : (
                  <>
                    <MapTrifold size={14} className="text-[#555] flex-shrink-0" />
                    <span className="text-[10px] text-[#555] flex-1">No floor plan — riders only see the grid for this barn</span>
                    <label className="px-2 py-1 bg-[#0A0A0A] border border-[#262626] rounded text-[10px] text-[#0096FF] hover:bg-[#0096FF]/10 hover:border-[#0096FF]/50 cursor-pointer flex items-center gap-1">
                      <UploadSimple size={10} />
                      {uploadingBarnIdx === bi ? 'Uploading…' : 'Upload Floor Plan'}
                      <input
                        type="file"
                        accept="image/*,application/pdf,.pdf"
                        disabled={uploadingBarnIdx === bi}
                        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleBarnDetailUpload(bi, f); }}
                        className="hidden"
                        data-testid={`${type}-barn-floorplan-upload-${bi}`}
                      />
                    </label>
                  </>
                )}
              </div>
              {(() => {
                const totalSpots = barn.rows.length;
                const firstNum = barn.rows[0]?.start_number ?? 1;
                const lastRow = barn.rows[barn.rows.length - 1];
                const lastNum = (lastRow?.start_number || 1) + ((lastRow?.spots || 1) - 1);
                // Default to collapsed for big barns unless producer has toggled.
                const isExpanded = expandedBarnRows[bi] ?? !manyRows;
                return (
                  <>
                    <button
                      type="button"
                      onClick={() => toggleBarnRowsExpanded(bi)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 bg-[#0F0F0F] hover:bg-[#161616] rounded text-[11px] text-white border border-[#262626] mb-2"
                      data-testid={`${type}-barn-rows-toggle-${bi}`}
                    >
                      <span>
                        <span className="font-semibold">{totalSpots} stall{totalSpots === 1 ? '' : 's'}</span>
                        {totalSpots > 0 && <span className="text-[#9E9E9E]"> · #{firstNum} – {lastNum}</span>}
                      </span>
                      <span className="text-[#9E9E9E] flex items-center gap-1">
                        {isExpanded ? 'Hide rows' : 'Edit rows'}
                        <CaretDown size={10} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                      </span>
                    </button>
                    {isExpanded && (
                      <div className="space-y-1.5 mb-2 max-h-[480px] overflow-y-auto pr-1">
                        {barn.rows.map((row, ri) => (
                          <div key={ri} className="flex items-center gap-2" data-testid={`${type}-row-${bi}-${ri}`}>
                            <span className="text-[10px] text-[#555] w-8">Row</span>
                            <input
                              value={row.label}
                              onChange={(e) => updateRow(bi, ri, 'label', e.target.value)}
                              className="w-14 px-2 py-1 bg-[#161616] border border-[#262626] rounded text-white text-xs text-center"
                              data-testid={`${type}-row-label-${bi}-${ri}`}
                            />
                            <input
                        type="number" min="1" value={row.start_number || 1}
                        onChange={(e) => updateRow(bi, ri, 'start_number', e.target.value)}
                        className="w-16 px-2 py-1 bg-[#161616] border border-[#262626] rounded text-white text-xs text-center"
                        title="Starting stall number"
                        data-testid={`${type}-row-start-${bi}-${ri}`}
                      />
                      <span className="text-[10px] text-[#555]">start</span>
                      <input
                        type="number" min="1" value={row.spots}
                        onChange={(e) => updateRow(bi, ri, 'spots', e.target.value)}
                        className="w-14 px-2 py-1 bg-[#161616] border border-[#262626] rounded text-white text-xs text-center"
                        data-testid={`${type}-row-spots-${bi}-${ri}`}
                      />
                      <span className="text-[10px] text-[#555]">spots</span>
                      <button type="button" onClick={() => removeRow(bi, ri)} className="ml-auto p-0.5 text-[#FF3B30]/60 hover:text-[#FF3B30]">
                        <Trash size={12} />
                      </button>
                    </div>
                  ))}
                      </div>
                    )}
                    {isExpanded && (
                      <button
                        type="button"
                        onClick={() => addRow(bi)}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] text-[#9E9E9E] hover:text-white transition-colors"
                        data-testid={`${type}-add-row-${bi}`}
                      >
                        <Plus size={10} /> Add Row
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
            );
          })}
        </div>
      )}

      {/* Preview */}
      {barns.length > 0 && (
        <div className="mb-3 p-3 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg overflow-x-auto">
          <p className="text-[10px] text-[#555] mb-2 uppercase tracking-wider">Preview</p>
          <div className="space-y-3">
            {barns.map((barn, bi) => {
              const totalSpots = barn.rows.reduce((s, r) => s + r.spots, 0);
              const manyRows = barn.rows.length > 20;
              return (
              <div key={bi}>
                <p className="text-[10px] font-bold text-[#FF9500] mb-1">{barn.barn} <span className="text-[#555]">({totalSpots} stalls)</span></p>
                <div className="space-y-1 ml-1">
                  {(manyRows ? barn.rows.slice(0, 3) : barn.rows).map((row, ri) => {
                    const customLabels = row.custom_labels || {};
                    const resolvedLabels = Array.from({ length: row.spots }, (_, i) => {
                      const c = customLabels[i];
                      return (c != null && c !== '') ? String(c) : String((row.start_number || 1) + i);
                    });
                    const hasCustom = Object.keys(customLabels).some(k => customLabels[k] != null && customLabels[k] !== '');
                    return (
                    <div key={ri}>
                      <div className="flex items-center gap-1">
                        <span className="text-[9px] text-[#0096FF] font-bold w-5 flex-shrink-0">{row.label}</span>
                        <div className="flex gap-0.5 flex-wrap">
                          {Array.from({ length: Math.min(row.spots, 40) }, (_, i) => {
                            const num = resolvedLabels[i];
                            const isCustom = customLabels[i] != null && customLabels[i] !== '';
                            return (
                              <button
                                key={i}
                                type="button"
                                onClick={() => {
                                  const next = window.prompt(
                                    `Stall # for ${barn.barn} row ${row.label}, position ${i + 1}\n\nLeave blank to use the auto-numbered value (${(row.start_number || 1) + i}).`,
                                    customLabels[i] != null ? String(customLabels[i]) : ''
                                  );
                                  if (next === null) return;
                                  const updated = { ...customLabels };
                                  const trimmed = next.trim();
                                  if (trimmed === '') delete updated[i]; else updated[i] = trimmed;
                                  updateRow(bi, ri, 'custom_labels', updated);
                                }}
                                className={`w-5 h-4 rounded-sm border flex items-center justify-center transition-colors cursor-pointer ${
                                  isCustom
                                    ? 'bg-[#FF9500]/20 border-[#FF9500]/60 hover:border-[#FF9500]'
                                    : 'bg-[#1A3A1A] border-[#2A5A2A] hover:border-[#34C759]'
                                }`}
                                title={`Click to edit · auto: ${(row.start_number || 1) + i}`}
                                data-testid={`${type}-cell-${bi}-${ri}-${i}`}
                              >
                                <span className={`text-[5px] ${isCustom ? 'text-[#FF9500] font-bold' : 'text-[#34C759]'}`}>{num}</span>
                              </button>
                            );
                          })}
                          {row.spots > 40 && <span className="text-[9px] text-[#555] self-center ml-1">+{row.spots - 40}</span>}
                        </div>
                      </div>
                      {/* Resolved numbering legend — helps producers sanity-check mixed sequential + custom labels */}
                      <div className="flex items-start gap-1 ml-6 mt-0.5" data-testid={`${type}-row-legend-${bi}-${ri}`}>
                        <span className={`text-[8px] uppercase tracking-wider flex-shrink-0 ${hasCustom ? 'text-[#FF9500]/80' : 'text-[#555]'}`}>
                          {hasCustom ? 'Final:' : 'Auto:'}
                        </span>
                        <span className="text-[9px] text-[#9E9E9E] leading-tight break-all">
                          {resolvedLabels.join(', ')}
                        </span>
                      </div>
                    </div>
                    );
                  })}
                  {manyRows && (
                    <p className="text-[9px] text-[#555] ml-6">…{barn.rows.length - 3} more stalls (preview collapsed)</p>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={addBarn}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-xs text-[#9E9E9E] hover:text-white hover:border-[#FF9500]/50 transition-colors"
          data-testid={`${type}-add-barn`}>
          <Buildings size={12} /> Add {type === 'stall' ? 'Barn' : 'Section'}
        </button>
        <button type="button" onClick={addMultipleBarns}
          className="flex items-center gap-1 px-3 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-xs text-[#9E9E9E] hover:text-white hover:border-[#FF9500]/50 transition-colors"
          data-testid={`${type}-add-multiple-barns`}>
          <Copy size={12} /> Add Multiple
        </button>
        {/* Always show Save button so producers can persist an emptied chart (deleting all barns). */}
        <button type="button" onClick={saveChart} disabled={saving}
          className="flex items-center gap-1 px-4 py-1.5 bg-[#0096FF] rounded text-xs text-white font-semibold hover:bg-[#0096FF]/80 transition-colors disabled:opacity-50"
          data-testid={`${type}-save-chart`}>
          {saving ? 'Saving...' : (barns.length === 0 ? 'Save (Clear All)' : 'Save Chart')}
        </button>
      </div>

      {/* Grid Configuration Modal */}
      {pendingGridRect && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={cancelGrid} data-testid="grid-config-modal">
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-white mb-3" style={{ fontFamily: 'Outfit' }}>Create Stall Grid</h3>
            <p className="text-[11px] text-[#9E9E9E] mb-3">
              The rectangle you drew will be split into <strong className="text-white">{(parseInt(gridConfig.rows, 10) || 1) * (parseInt(gridConfig.cols, 10) || 1)} stalls</strong> arranged as {gridConfig.rows || 1} rows × {gridConfig.cols || 1} columns.
            </p>

            <label className="block text-[10px] text-[#9E9E9E] mb-1">Barn / Section Name</label>
            <input value={gridConfig.barn} onChange={(e) => setGridConfig({ ...gridConfig, barn: e.target.value })}
              placeholder="e.g. Barn A, North Row, West Wing"
              className="w-full px-2 py-1.5 mb-3 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm focus:outline-none focus:border-[#FF9500]"
              data-testid="grid-config-barn" autoFocus />

            <div className="grid grid-cols-3 gap-2 mb-4">
              <div>
                <label className="block text-[10px] text-[#9E9E9E] mb-1">Rows</label>
                <input type="number" min="1" max="50" value={gridConfig.rows} onChange={(e) => setGridConfig({ ...gridConfig, rows: e.target.value })}
                  className="w-full px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm text-center focus:outline-none focus:border-[#FF9500]"
                  data-testid="grid-config-rows" />
              </div>
              <div>
                <label className="block text-[10px] text-[#9E9E9E] mb-1">Columns</label>
                <input type="number" min="1" max="100" value={gridConfig.cols} onChange={(e) => setGridConfig({ ...gridConfig, cols: e.target.value })}
                  className="w-full px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm text-center focus:outline-none focus:border-[#FF9500]"
                  data-testid="grid-config-cols" />
              </div>
              <div>
                <label className="block text-[10px] text-[#9E9E9E] mb-1">Start #</label>
                <input type="number" min="1" value={gridConfig.start} onChange={(e) => setGridConfig({ ...gridConfig, start: e.target.value })}
                  className="w-full px-2 py-1.5 bg-[#0A0A0A] border border-[#262626] rounded text-white text-sm text-center focus:outline-none focus:border-[#FF9500]"
                  data-testid="grid-config-start" />
              </div>
            </div>

            <p className="text-[10px] text-[#555] mb-3">
              Stalls numbered {gridConfig.start || 1} through {(parseInt(gridConfig.start, 10) || 1) + (parseInt(gridConfig.rows, 10) || 1) * (parseInt(gridConfig.cols, 10) || 1) - 1}.
              Numbers fill rows left-to-right, top-to-bottom.
            </p>

            <div className="flex gap-2">
              <button type="button" onClick={cancelGrid}
                className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded text-xs text-[#9E9E9E] hover:text-white"
                data-testid="grid-config-cancel">Cancel</button>
              <button type="button" onClick={confirmGrid}
                className="flex-1 px-3 py-2 bg-[#FF9500] rounded text-xs text-white font-semibold hover:bg-[#FF9500]/80"
                data-testid="grid-config-confirm">Create Grid</button>
            </div>
          </div>
        </div>
      )}

      {/* Per-barn interactive stall placement editor. Render whenever a barn
          is being edited regardless of whether it has a floor plan yet —
          BarnMapEditor's v83 empty-state lets the producer upload one from
          inside the modal, so we don't want to gate the mount on
          detail_map_url. */}
      {editingBarnIdx !== null && barns[editingBarnIdx] && (
        <BarnMapEditor
          showId={showId}
          mapType={type}
          barnIdx={editingBarnIdx}
          barnName={barns[editingBarnIdx].barn || `Barn ${editingBarnIdx + 1}`}
          totalSpots={(barns[editingBarnIdx].rows || []).reduce((s, r) => s + r.spots, 0)}
          detailMapUrl={barns[editingBarnIdx].detail_map_url}
          initialMarkers={barns[editingBarnIdx].detail_markers || []}
          onClose={() => setEditingBarnIdx(null)}
          onSaved={(saved) => {
            setBarns(prev => {
              const next = [...prev];
              if (next[editingBarnIdx]) next[editingBarnIdx] = { ...next[editingBarnIdx], detail_markers: saved };
              return next;
            });
            // Keep the editor open so the producer can visually verify the
            // saved state. Don't call loadChart() here — that triggers a
            // re-render that occasionally unmounts BarnMapEditor.
          }}
        />
      )}
    </div>
  );
};


export const FacilityTemplatePanel = ({ showId }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [saveName, setSaveName] = useState('');
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(null);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const { data } = await api.get('/facility-templates');
      setTemplates(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const saveAsTemplate = async () => {
    if (!saveName.trim()) { toast.error('Enter a facility name'); return; }
    setSaving(true);
    try {
      await api.post('/facility-templates', { facility_name: saveName, show_id: showId });
      toast.success(`Saved "${saveName}" as facility template`);
      setSaveName('');
      loadTemplates();
    } catch { toast.error('Failed to save template'); }
    finally { setSaving(false); }
  };

  const applyTemplate = async (templateId, name) => {
    setApplying(templateId);
    try {
      const { data } = await api.post(`/shows/${showId}/apply-facility-template/${templateId}`);
      toast.success(data.message);
      window.location.reload();
    } catch { toast.error('Failed to apply template'); }
    finally { setApplying(null); }
  };

  const deleteTemplate = async (templateId) => {
    try {
      await api.delete(`/facility-templates/${templateId}`);
      toast.success('Template deleted');
      setTemplates(templates.filter(t => t.id !== templateId));
    } catch { toast.error('Failed to delete template'); }
  };

  const filtered = templates.filter(t =>
    t.facility_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="bg-[#161616] border border-[#262626] rounded-lg p-4 lg:col-span-2" data-testid="facility-template-panel">
      <div className="flex items-center gap-2 mb-3">
        <Buildings size={18} className="text-[#AF52DE]" />
        <h4 className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit' }}>Facility Templates</h4>
      </div>
      <p className="text-xs text-[#555] mb-3">Save this show's stall/RV layout as a template, or load a layout from another show at the same facility.</p>

      {/* Save current layout */}
      <div className="flex gap-2 mb-4">
        <input
          value={saveName}
          onChange={(e) => setSaveName(e.target.value)}
          placeholder="Facility name (e.g., Indiana State Fairgrounds)"
          className="flex-1 px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-xs focus:outline-none focus:border-[#AF52DE]"
          data-testid="template-save-name"
        />
        <button type="button" onClick={saveAsTemplate} disabled={saving}
          className="px-4 py-2 bg-[#AF52DE] rounded-lg text-xs text-white font-semibold hover:bg-[#AF52DE]/80 transition-colors disabled:opacity-50 whitespace-nowrap"
          data-testid="template-save-btn">
          {saving ? 'Saving...' : 'Save as Template'}
        </button>
      </div>

      {/* Search + load */}
      <div className="mb-3">
        <input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search facility templates..."
          className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#262626] rounded-lg text-white text-xs focus:outline-none focus:border-[#0096FF]"
          data-testid="template-search"
        />
      </div>

      {loading ? (
        <div className="shimmer h-8 w-32 rounded"></div>
      ) : filtered.length === 0 ? (
        <p className="text-[10px] text-[#555]">{searchQuery ? 'No templates match' : 'No facility templates saved yet'}</p>
      ) : (
        <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="template-list">
          {filtered.map((t) => (
            <div key={t.id} className="flex items-center gap-2 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-2.5" data-testid={`template-${t.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-white truncate">{t.facility_name}</p>
                <p className="text-[10px] text-[#555]">
                  {t.total_stalls} stalls · {t.total_rv} RV
                  {t.has_facility_map && ' · Map'}
                  {t.created_by_name && ` · by ${t.created_by_name}`}
                </p>
              </div>
              <button type="button" onClick={() => applyTemplate(t.id, t.facility_name)}
                disabled={applying === t.id}
                className="px-3 py-1 bg-[#0096FF] rounded text-[10px] text-white font-semibold hover:bg-[#0096FF]/80 transition-colors disabled:opacity-50 whitespace-nowrap"
                data-testid={`template-apply-${t.id}`}>
                {applying === t.id ? 'Applying...' : 'Load'}
              </button>
              <button type="button" onClick={() => deleteTemplate(t.id)}
                className="p-1 text-[#FF3B30]/50 hover:text-[#FF3B30]">
                <Trash size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
